import React, { useState, useRef, useEffect } from "react";
import { Home, Calculator, Activity, Trash2, Undo2, Download, Eye, Layers } from "lucide-react";
import { toast } from "sonner";

interface PlacedElement {
  id: string;
  type: "wall" | "electric" | "plumbing" | "furniture";
  name?: string;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  x?: number;
  y?: number;
}

export const ArchitectureSuite = () => {
  const [activeSubTab, setActiveSubTab] = useState<"layout" | "calc" | "truss" | "topo" | "budget" | "block3d" | "aerodynamics" | "rain-harvest" | "thermal">("layout");

  // Thermal Gradient States
  const [thermalOutTemp, setThermalOutTemp] = useState<number>(38);
  const [thermalInTemp, setThermalInTemp] = useState<number>(24);
  const [thermalAlbedo, setThermalAlbedo] = useState<number>(0.15); // dark 0.15, white 0.85
  const [thermalConcreteInches, setThermalConcreteInches] = useState<number>(6);
  const [thermalInsulationInches, setThermalInsulationInches] = useState<number>(0);
  const [thermalDrywallEnabled, setThermalDrywallEnabled] = useState<boolean>(false);

  const [assemblyStep, setAssemblyStep] = useState<number>(0);

  // --- 12. Rainwater Harvesting & Gravity Filtration (150% Pro Feature) ---
  const [leafClogPct, setLeafClogPct] = useState<number>(10); // %
  const [filterGravelStatus, setFilterGravelStatus] = useState<number>(100); // % cleanliness
  const [filterCarbonStatus, setFilterCarbonStatus] = useState<number>(100); // % cleanliness
  const [uvLampWatts, setUvLampWatts] = useState<number>(15); // Watts

  // --- 11. Roof Aerodynamics Simulator States (140% Pro Feature) ---
  const [roofType, setRoofType] = useState<"flat" | "gable" | "hip">("flat");
  const [windSpeedMph, setWindSpeedMph] = useState<number>(120);

  // --- 5. Presupuesto Civil (PR) State ---
  const [blocksQty, setBlocksQty] = useState<number>(500);
  const [cementBags, setCementBags] = useState<number>(30);
  const [sandM3, setSandM3] = useState<number>(4);
  const [gravelM3, setGravelM3] = useState<number>(6);
  const [rebarQty, setRebarQty] = useState<number>(20);
  const [conduitQty, setConduitQty] = useState<number>(15);
  const [pexQty, setPexQty] = useState<number>(10);
  const [hourlyRate, setHourlyRate] = useState<number>(15); // $15/hr
  const [hoursWorked, setHoursWorked] = useState<number>(40);
  const [mixerDays, setMixerDays] = useState<number>(3);
  const [autofillSuccess, setAutofillSuccess] = useState<string | null>(null);

  // --- 1. Planificador de Redes 2D State ---
  const [layoutTool, setLayoutTool] = useState<"wall" | "electric" | "plumbing" | "furniture">("wall");
  const [selectedFurniture, setSelectedFurniture] = useState<string>("Sofa");
  const [elements, setElements] = useState<PlacedElement[]>([]);
  const [drawingStart, setDrawingStart] = useState<{ x: number; y: number } | null>(null);
  const [tempDrawing, setTempDrawing] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const canvasRef = useRef<SVGSVGElement>(null);

  // --- 2. Calculadora Estructural State ---
  const [calcType, setCalcType] = useState<"brick" | "concrete" | "pipe" | "tile" | "water">("brick");
  // Concrete Block variables (PR standard)
  const [wallLength, setWallLength] = useState<number>(10); // meters
  const [wallHeight, setWallHeight] = useState<number>(3); // meters
  const [blockThickness, setBlockThickness] = useState<string>("6"); // "4" | "6" | "8" inches
  const [rebarSpacing, setRebarSpacing] = useState<number>(24); // every 16", 24", or 32" inches
  const [rebarSize, setRebarSize] = useState<string>("3/8"); // "3/8" or "1/2" inches

  // Rainwater cistern variables (Autosustentable)
  const [roofLength, setRoofLength] = useState<number>(12); // meters
  const [roofWidth, setRoofWidth] = useState<number>(10); // meters
  const [annualRainfall, setAnnualRainfall] = useState<number>(1650); // mm (65 inches)
  const [cisternCapacity, setCisternCapacity] = useState<number>(1000); // gallons
  const [dailyConsumption, setDailyConsumption] = useState<number>(120); // gallons
  const [rainHarvestEfficiency, setRainHarvestEfficiency] = useState<number>(85); // %

  // Listener for Prometheus Autómata automation events
  useEffect(() => {
    const handleAction = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && detail.action === "SET") {
        const { key, value } = detail;
        if (key === "wallLength") setWallLength(Number(value));
        else if (key === "wallHeight") setWallHeight(Number(value));
        else if (key === "blockThickness") setBlockThickness(String(value));
        else if (key === "rebarSpacing") setRebarSpacing(Number(value));
        else if (key === "rebarSize") setRebarSize(String(value));
        else if (key === "slabLength") setSlabLength(Number(value));
        else if (key === "slabWidth") setSlabWidth(Number(value));
        else if (key === "slabThickness") setSlabThickness(Number(value));
        else if (key === "lineLength") setLineLength(Number(value));
        else if (key === "tileAreaLength") setTileAreaLength(Number(value));
        else if (key === "tileAreaWidth") setTileAreaWidth(Number(value));
        else if (key === "tilePreset") {
          const val = String(value);
          setTilePreset(val);
          if (val === "30x30") { setTileWidthCm(30); setTileHeightCm(30); }
          else if (val === "45x45") { setTileWidthCm(45); setTileHeightCm(45); }
          else if (val === "60x60") { setTileWidthCm(60); setTileHeightCm(60); }
          else if (val === "30x60") { setTileWidthCm(30); setTileHeightCm(60); }
        }
        else if (key === "tilePattern") setTilePattern(String(value));
        else if (key === "tileCostPerSqM") setTileCostPerSqM(Number(value));
        else if (key === "roofLength") setRoofLength(Number(value));
        else if (key === "roofWidth") setRoofWidth(Number(value));
        else if (key === "annualRainfall") setAnnualRainfall(Number(value));
        else if (key === "cisternCapacity") setCisternCapacity(Number(value));
        else if (key === "dailyConsumption") setDailyConsumption(Number(value));
      }
    };
    window.addEventListener("prometheus-action", handleAction);
    return () => window.removeEventListener("prometheus-action", handleAction);
  }, []);
  // Concrete variables
  const [slabLength, setSlabLength] = useState<number>(6); // meters
  const [slabWidth, setSlabWidth] = useState<number>(5); // meters
  const [slabThickness, setSlabThickness] = useState<number>(0.15); // meters
  const [concreteMixType, setConcreteMixType] = useState<"structural" | "mortar">("structural");

  // Water quality / Plomeria variables
  const [pHValue, setPHValue] = useState<number>(7.0);
  const [tdsPPM, setTdsPPM] = useState<number>(150);
  const [plumbPipeDiameter, setPlumbPipeDiameter] = useState<number>(0.75); // inches
  const [plumbPumpHP, setPlumbPumpHP] = useState<number>(0.5); // HP

  // Pipelines
  const [lineLength, setLineLength] = useState<number>(45); // meters

  // Tile calculator variables
  const [tileAreaLength, setTileAreaLength] = useState<number>(6); // meters
  const [tileAreaWidth, setTileAreaWidth] = useState<number>(5); // meters
  const [tilePreset, setTilePreset] = useState<string>("60x60"); // "30x30", "45x45", "60x60", "30x60", "custom"
  const [tileWidthCm, setTileWidthCm] = useState<number>(60);
  const [tileHeightCm, setTileHeightCm] = useState<number>(60);
  const [tileJointMm, setTileJointMm] = useState<number>(3); // 3mm joint
  const [tileWastePercent, setTileWastePercent] = useState<number>(10); // 10% default waste margin
  const [tilePattern, setTilePattern] = useState<string>("orthogonal"); // "orthogonal", "offset", "diagonal"
  const [tileCostPerSqM, setTileCostPerSqM] = useState<number>(24); // $24/m2 average cost

  // --- 3. Simulador de Cerchas State ---
  const [trussStyle, setTrussStyle] = useState<"pratt" | "howe" | "warren">("pratt");
  const [appliedLoad, setAppliedLoad] = useState<number>(800); // Newtons
  const [trussSpan, setTrussSpan] = useState<number>(300); // pixels width

  // --- 4. Topografía 3D (Applitop mock) State ---
  const [elevNW, setElevNW] = useState<number>(20); // NW height
  const [elevNE, setElevNE] = useState<number>(5);  // NE height
  const [elevSW, setElevSW] = useState<number>(10); // SW height
  const [elevSE, setElevSE] = useState<number>(35); // SE height
  const [topoMode, setTopoMode] = useState<"points" | "grid" | "contours">("grid");
  const topoCanvasRef = useRef<HTMLCanvasElement>(null);
  const topoAnimFrameRef = useRef<number>(0);
  const topoAngleRef = useRef<number>(0.5); // Rotation angle

  // 1. Grid Snapping Helper
  const snapToGrid = (val: number, step = 10) => {
    return Math.round(val / step) * step;
  };

  const getCanvasCoords = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const x = snapToGrid(e.clientX - rect.left);
    const y = snapToGrid(e.clientY - rect.top);
    return { x, y };
  };

  // 2. Mouse Drawing handlers
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    const coords = getCanvasCoords(e);

    if (layoutTool === "furniture") {
      const newItem: PlacedElement = {
        id: `furniture-${Date.now()}`,
        type: "furniture",
        name: selectedFurniture,
        x: coords.x,
        y: coords.y,
      };
      setElements((prev) => [...prev, newItem]);
    } else {
      setDrawingStart(coords);
      setTempDrawing({ x1: coords.x, y1: coords.y, x2: coords.x, y2: coords.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!drawingStart || !tempDrawing) return;
    const coords = getCanvasCoords(e);
    setTempDrawing({ ...tempDrawing, x2: coords.x, y2: coords.y });
  };

  const handleMouseUp = () => {
    if (!drawingStart || !tempDrawing) return;
    const dx = tempDrawing.x2 - tempDrawing.x1;
    const dy = tempDrawing.y2 - tempDrawing.y1;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
      const newElem: PlacedElement = {
        id: `${layoutTool}-${Date.now()}`,
        type: layoutTool,
        x1: tempDrawing.x1,
        y1: tempDrawing.y1,
        x2: tempDrawing.x2,
        y2: tempDrawing.y2,
      };
      setElements((prev) => [...prev, newElem]);
    }
    setDrawingStart(null);
    setTempDrawing(null);
  };

  const handleUndo = () => {
    setElements((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    setElements([]);
  };

  // Export CAD Layout SVG
  const handleExportSVG = () => {
    if (!canvasRef.current) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(canvasRef.current);
    const svgBlob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const downloadLink = document.createElement("a");
    downloadLink.href = svgUrl;
    downloadLink.download = "layout_arquitectura_prometheus.svg";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleAutofillFromLayout = () => {
    let wallMeters = 0;
    let electricMeters = 0;
    let plumbingMeters = 0;

    elements.forEach((elem) => {
      if (elem.x1 !== undefined && elem.y1 !== undefined && elem.x2 !== undefined && elem.y2 !== undefined) {
        const pxLen = Math.sqrt(Math.pow(elem.x2 - elem.x1, 2) + Math.pow(elem.y2 - elem.y1, 2));
        const meters = pxLen / 50; // 50px = 1m
        if (elem.type === "wall") wallMeters += meters;
        if (elem.type === "electric") electricMeters += meters;
        if (elem.type === "plumbing") plumbingMeters += meters;
      }
    });

    if (wallMeters === 0 && electricMeters === 0 && plumbingMeters === 0) {
      setAutofillSuccess("El plano 2D está vacío. Dibuja muros y cables primero.");
      setTimeout(() => setAutofillSuccess(null), 4000);
      return;
    }

    const calculatedBlocks = Math.ceil(wallMeters * 3 * 12.5);
    const calculatedCement = Math.ceil(calculatedBlocks / 35) + 5;
    const calculatedSand = Math.max(1, Math.round((calculatedBlocks * 0.001) * 10) / 10);
    const calculatedGravel = Math.max(1, Math.round(calculatedSand * 1.2 * 10) / 10);
    const calculatedRebar = Math.ceil(wallMeters / 1.5) * 3;
    const calculatedConduits = Math.ceil(electricMeters / 3) + 2;
    const calculatedPex = Math.ceil(plumbingMeters / 3) + 2;

    setBlocksQty(calculatedBlocks);
    setCementBags(calculatedCement);
    setSandM3(calculatedSand);
    setGravelM3(calculatedGravel);
    setRebarQty(calculatedRebar);
    setConduitQty(calculatedConduits);
    setPexQty(calculatedPex);

    setAutofillSuccess(
      `¡Sincronizado! Detectados: ${wallMeters.toFixed(1)}m muros (${calculatedBlocks} bloques), ` +
      `${electricMeters.toFixed(1)}m electricidad (${calculatedConduits} tubos) y ` +
      `${plumbingMeters.toFixed(1)}m plomería (${calculatedPex} tubos).`
    );
    setTimeout(() => setAutofillSuccess(null), 5000);
  };

  // Budget calculations
  const matBlocksCost = blocksQty * 1.85;
  const matCementCost = cementBags * 9.25;
  const matSandCost = sandM3 * 45.0;
  const matGravelCost = gravelM3 * 48.0;
  const matRebarCost = rebarQty * 8.5;
  const matConduitsCost = conduitQty * 3.25;
  const matPexCost = pexQty * 4.5;

  const subtotalMaterials = matBlocksCost + matCementCost + matSandCost + matGravelCost + matRebarCost + matConduitsCost + matPexCost;
  const subtotalLabor = hourlyRate * hoursWorked;
  const subtotalEquipment = mixerDays * 65.0;
  const grandTotalBudget = subtotalMaterials + subtotalLabor + subtotalEquipment;

  // 3. Structural Estimator Calculations (Concrete Blocks - PR standard)
  const wallAreaSqM = wallLength * wallHeight;
  const totalBlocksNeeded = Math.ceil((wallAreaSqM / 0.08) * 1.05); // 5% waste margin
  const mortarMultiplier = blockThickness === "8" ? 0.014 : blockThickness === "4" ? 0.008 : 0.011;
  const blockMortarCubicM = Math.round(wallAreaSqM * mortarMultiplier * 100) / 100;

  // Grout concrete to fill vertical steel cells
  const wallLengthInches = (wallLength * 3.28084) * 12;
  const rebarColsCount = Math.ceil(wallLengthInches / (rebarSpacing || 24)) + 1;
  const fillVolumePerMeter = blockThickness === "8" ? 0.008 : blockThickness === "4" ? 0.0035 : 0.0055;
  const cellFillConcreteCubicM = Math.round(rebarColsCount * wallHeight * fillVolumePerMeter * 100) / 100;

  // Steel Rebar (varillas de 20 pies / 6.1m standard PR length)
  const verticalRebarLengthM = rebarColsCount * wallHeight * 1.1; // 10% overlap
  const horizontalRebarRows = Math.floor((wallHeight / 0.20) / 3); // every 3 courses (60cm)
  const horizontalRebarLengthM = horizontalRebarRows * wallLength * 1.1;
  const totalRebarLengthM = verticalRebarLengthM + horizontalRebarLengthM;
  const totalRebarRods20ft = Math.ceil(totalRebarLengthM / 6.1);
  
  // Tie Wire (Alambre de Amarre #16)
  const rebarJunctions = rebarColsCount * Math.max(1, horizontalRebarRows);
  const tieWireLbs = Number((rebarJunctions * 0.03).toFixed(1)); // ~0.03 lbs per tie

  // Cost estimates based on local PR suppliers (National Lumber / Home Depot)
  const blockUnitPrice = blockThickness === "8" ? 2.10 : blockThickness === "6" ? 1.85 : 1.65;
  const rebarUnitPrice = rebarSize === "1/2" ? 9.20 : 6.50; // #4 is $9.20, #3 is $6.50
  
  const estimatedBlocksCost = totalBlocksNeeded * blockUnitPrice;
  const estimatedRebarCost = totalRebarRods20ft * rebarUnitPrice;
  const estimatedTieWireCost = Math.ceil(tieWireLbs) * 2.50; // $2.50/lb

  // Mortar/Grout cement requirements: ~8.5 bags of 42.5kg per m3
  const totalWallMortarGroutM3 = blockMortarCubicM + cellFillConcreteCubicM;
  const wallCementBags = Math.ceil(totalWallMortarGroutM3 * 8.5);
  const estimatedCementCost = wallCementBags * 9.25; // $9.25 per bag of Portland

  const totalCalculatedWallCost = estimatedBlocksCost + estimatedRebarCost + estimatedTieWireCost + estimatedCementCost;

  const slabVolumeCubicM = Math.round(slabLength * slabWidth * slabThickness * 100) / 100;
  
  // Concrete mix outputs (in bags)
  const isStructural = concreteMixType === "structural";
  const volYards = slabVolumeCubicM * 1.30795;
  const cementBags47lb = Math.ceil(volYards * (isStructural ? 9 : 12));
  const sandBags50lb = Math.ceil(volYards * (isStructural ? 27 : 48));
  const gravelBags50lb = isStructural ? Math.ceil(volYards * 36) : 0;
  const waterGalsNeeded = Math.ceil(volYards * (isStructural ? 45 : 60));

  // Hydraulics
  const flowGPM = plumbPumpHP === 1.0 ? 22 : plumbPumpHP === 0.75 ? 15 : 10;
  const flowLPM = flowGPM * 3.78541;
  const waterVelocityFps = (flowGPM * 0.408) / Math.pow(plumbPipeDiameter, 2);
  // Hazen-Williams Friction Loss in PSI (C=150 for plastic PVC)
  const frictionLossPSI = 4.52 * (lineLength * 3.28084) * Math.pow(flowGPM, 1.85) / (Math.pow(150, 1.85) * Math.pow(plumbPipeDiameter, 4.87));
  const pipePressurePSI = Math.max(0, 45 - frictionLossPSI);

  const total3mPipes = Math.ceil(lineLength / 3);
  const total50mCableRolls = Math.ceil(lineLength / 50);

  // Tile calculator formulas
  const tileAreaSqM = Math.round(tileAreaLength * tileAreaWidth * 100) / 100;
  const singleTileAreaSqM = (tileWidthCm / 100) * (tileHeightCm / 100);
  const baseTilesNeeded = Math.ceil(tileAreaSqM / (singleTileAreaSqM || 0.01));
  const actualWastePercent = tilePattern === "diagonal" ? Math.max(15, tileWastePercent) : tileWastePercent;
  const totalTilesNeeded = Math.ceil(baseTilesNeeded * (1 + actualWastePercent / 100));
  const coveragePerBox = tilePreset === "30x30" ? 1.08 : tilePreset === "45x45" ? 1.22 : tilePreset === "60x60" ? 1.44 : tilePreset === "30x60" ? 1.44 : 1.20;
  const totalTileBoxes = Math.ceil((tileAreaSqM * (1 + actualWastePercent / 100)) / (coveragePerBox || 1.0));
  const totalTileCostEstimate = Math.round(tileAreaSqM * tileCostPerSqM * (1 + actualWastePercent / 100) * 100) / 100;

  // Pluvial collection formulas (Cisterna)
  const roofAreaSqM = roofLength * roofWidth;
  const roofAreaSqFt = Math.round(roofAreaSqM * 10.7639 * 10) / 10;
  const annualRainfallM = annualRainfall / 1000;
  const annualRainfallInches = Math.round((annualRainfall / 25.4) * 10) / 10;
  const harvestPotentialM3 = roofAreaSqM * annualRainfallM * (rainHarvestEfficiency / 100);
  const harvestPotentialGallons = Math.round(harvestPotentialM3 * 264.172);
  const harvestPotentialLiters = Math.round(harvestPotentialM3 * 1000);
  const dailyConsumptionLiters = Math.round(dailyConsumption * 3.78541);
  const autonomyDays = dailyConsumption > 0 ? Math.round(cisternCapacity / dailyConsumption) : 0;
  const recommendedCisternCapacityGallons = dailyConsumption * 30;
  const recommendedCisternCapacityLiters = Math.round(recommendedCisternCapacityGallons * 3.78541);

  // 4. Truss Load Simulator rendering
  const deformation = (appliedLoad / 2000) * 10;
  
  const drawTruss = () => {
    const startX = 60;
    const endX = startX + trussSpan;
    const bottomY = 180;
    const topY = bottomY - 80;

    const midX = (startX + endX) / 2;
    const quarterX = startX + trussSpan / 4;
    const threeQuarterX = startX + (trussSpan * 3) / 4;

    const nodes = {
      A: { x: startX, y: bottomY },
      B: { x: quarterX, y: bottomY + deformation * 0.4 },
      C: { x: midX, y: bottomY + deformation * 0.9 },
      D: { x: threeQuarterX, y: bottomY + deformation * 0.4 },
      E: { x: endX, y: bottomY },
      F: { x: quarterX, y: topY + deformation * 0.6 },
      G: { x: midX, y: topY + deformation },
      H: { x: threeQuarterX, y: topY + deformation * 0.6 }
    };

    const members = [
      { from: nodes.A, to: nodes.B, type: "tension" },
      { from: nodes.B, to: nodes.C, type: "tension" },
      { from: nodes.C, to: nodes.D, type: "tension" },
      { from: nodes.D, to: nodes.E, type: "tension" },
      { from: nodes.A, to: nodes.F, type: "compression" },
      { from: nodes.F, to: nodes.G, type: "compression" },
      { from: nodes.G, to: nodes.H, type: "compression" },
      { from: nodes.H, to: nodes.E, type: "compression" },
      { from: nodes.B, to: nodes.F, type: trussStyle === "howe" ? "tension" : "compression" },
      { from: nodes.C, to: nodes.G, type: "tension" },
      { from: nodes.D, to: nodes.H, type: trussStyle === "howe" ? "tension" : "compression" },
      { from: nodes.A, to: nodes.B, type: "neutral" },
      { from: nodes.B, to: nodes.G, type: trussStyle === "pratt" ? "tension" : "compression" },
      { from: nodes.D, to: nodes.G, type: trussStyle === "pratt" ? "tension" : "compression" }
    ];

    return { nodes, members };
  };

  const { nodes, members } = drawTruss();

  // --- 5. 3D Topography Canvas render loop ---
  useEffect(() => {
    if (activeSubTab !== "topo") return;

    const renderTopo = () => {
      const canvas = topoCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Update rotation angle
      topoAngleRef.current += 0.006;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Grid specifications
      const gridCount = 10;
      const size = 180;
      const step = size / (gridCount - 1);
      const startX = -size / 2;
      const startZ = -size / 2;

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2 + 10;

      // Calculate 3D points
      const points3D: { px: number; py: number; rawY: number }[][] = [];

      for (let i = 0; i < gridCount; i++) {
        points3D[i] = [];
        for (let j = 0; j < gridCount; j++) {
          const gx = startX + i * step;
          const gz = startZ + j * step;

          // Bilinear interpolation of heights
          const tx = i / (gridCount - 1);
          const ty = j / (gridCount - 1);
          const rawH = (1 - tx) * (1 - ty) * elevNW + tx * (1 - ty) * elevNE + (1 - tx) * ty * elevSW + tx * ty * elevSE;

          // Scale height
          const y = rawH * 1.5;

          // Rotate around Y axis
          const cos = Math.cos(topoAngleRef.current);
          const sin = Math.sin(topoAngleRef.current);
          const rx = gx * cos - gz * sin;
          const rz = gx * sin + gz * cos;

          // Isometric Orthographic projection
          const px = centerX + rx;
          const py = centerY - y + rz * 0.45;

          points3D[i][j] = { px, py, rawY: rawH };
        }
      }

      // Draw depending on mode
      ctx.lineWidth = 1;

      if (topoMode === "points") {
        // Point Cloud Mode
        for (let i = 0; i < gridCount; i++) {
          for (let j = 0; j < gridCount; j++) {
            const pt = points3D[i][j];
            
            // Color based on height (Green Valley -> Red Peak)
            const red = Math.min(255, Math.floor((pt.rawY / 40) * 255));
            const green = Math.max(0, 255 - Math.floor((pt.rawY / 40) * 150));
            const blue = 100 + Math.floor((pt.rawY / 40) * 100);

            ctx.fillStyle = `rgb(${red}, ${green}, ${blue})`;
            ctx.beginPath();
            ctx.arc(pt.px, pt.py, 2.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      } else if (topoMode === "grid") {
        // Wireframe mesh Mode
        ctx.strokeStyle = "rgba(0, 245, 255, 0.4)";
        for (let i = 0; i < gridCount; i++) {
          for (let j = 0; j < gridCount; j++) {
            // Draw connection to next i
            if (i < gridCount - 1) {
              ctx.beginPath();
              ctx.moveTo(points3D[i][j].px, points3D[i][j].py);
              ctx.lineTo(points3D[i+1][j].px, points3D[i+1][j].py);
              ctx.stroke();
            }
            // Draw connection to next j
            if (j < gridCount - 1) {
              ctx.beginPath();
              ctx.moveTo(points3D[i][j].px, points3D[i][j].py);
              ctx.lineTo(points3D[i][j+1].px, points3D[i][j+1].py);
              ctx.stroke();
            }
          }
        }
      } else if (topoMode === "contours") {
        // Contour line slices
        ctx.strokeStyle = "#FF6B35";
        ctx.lineWidth = 1.5;
        // Slice at discrete height levels
        const slices = [5, 10, 15, 20, 25, 30, 35];
        
        // Simple marching contour grid approximation
        for (let s of slices) {
          for (let i = 0; i < gridCount - 1; i++) {
            for (let j = 0; j < gridCount - 1; j++) {
              const p0 = points3D[i][j];
              const p1 = points3D[i+1][j];
              const p2 = points3D[i][j+1];

              // If slice level lies between heights, draw connections
              const checkDraw = (v1: number, v2: number) => {
                return (v1 >= s && v2 <= s) || (v1 <= s && v2 >= s);
              };

              if (checkDraw(p0.rawY, p1.rawY) && checkDraw(p0.rawY, p2.rawY)) {
                ctx.beginPath();
                ctx.moveTo((p0.px + p1.px) / 2, (p0.py + p1.py) / 2);
                ctx.lineTo((p0.px + p2.px) / 2, (p0.py + p2.py) / 2);
                ctx.stroke();
              }
            }
          }
        }
      }

      topoAnimFrameRef.current = requestAnimationFrame(renderTopo);
    };

    renderTopo();
    return () => cancelAnimationFrame(topoAnimFrameRef.current);
  }, [activeSubTab, elevNW, elevNE, elevSW, elevSE, topoMode]);

  return (
    <div className="bg-card/45 backdrop-blur-md border border-border/85 rounded-lg p-6 space-y-6">
      {/* Sub-Tab Navigation Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-border/40 pb-4 gap-4">
        <div className="flex items-center gap-2">
          <Home className="w-5 h-5 text-primary-glow" />
          <h4 className="font-serif-display text-lg text-foreground">Arquitectura & Estructuras</h4>
        </div>
        
        {/* Toggle options */}
        <div className="flex flex-nowrap overflow-x-auto whitespace-nowrap bg-void/90 border border-border p-1 rounded max-w-full gap-1 sm:gap-0 no-scrollbar">
          <button
            onClick={() => setActiveSubTab("layout")}
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded cursor-pointer transition-all ${
              activeSubTab === "layout" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Plano de Redes
          </button>
          <button
            onClick={() => setActiveSubTab("calc")}
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded cursor-pointer transition-all ${
              activeSubTab === "calc" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Calculadora Civil
          </button>
          <button
            onClick={() => setActiveSubTab("truss")}
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded cursor-pointer transition-all ${
              activeSubTab === "truss" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Análisis de Cerchas
          </button>
          <button
            onClick={() => setActiveSubTab("topo")}
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded cursor-pointer transition-all ${
              activeSubTab === "topo" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Topografía 3D
          </button>
          <button
            onClick={() => setActiveSubTab("budget")}
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded cursor-pointer transition-all ${
              activeSubTab === "budget" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Presupuesto Civil (PR)
          </button>
          <button
            onClick={() => setActiveSubTab("block3d")}
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded cursor-pointer transition-all ${
              activeSubTab === "block3d" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            🧱 Ensamble Sismorresistente 3D
          </button>
          <button
            onClick={() => setActiveSubTab("aerodynamics")}
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded cursor-pointer transition-all ${
              activeSubTab === "aerodynamics" ? "bg-primary text-primary-foreground font-bold shadow-[0_0_8px_var(--primary)]" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            🌪️ Aerodinámica de Techos
          </button>
          <button
            onClick={() => setActiveSubTab("rain-harvest")}
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded cursor-pointer transition-all ${
              activeSubTab === "rain-harvest" ? "bg-primary text-primary-foreground font-bold shadow-[0_0_8px_var(--primary)]" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            💧 Cosecha de Lluvia
          </button>
          <button
            onClick={() => setActiveSubTab("thermal")}
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded cursor-pointer transition-all ${
              activeSubTab === "thermal" ? "bg-primary text-primary-foreground font-bold shadow-[0_0_8px_var(--primary)]" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            🌡️ Gradiente Térmico
          </button>
        </div>
      </div>

      {activeSubTab === "layout" ? (
        /* 1. Planificador de Redes Layout */
        <div className="grid lg:grid-cols-12 gap-8 pt-2">
          {/* Left panel: layout tools */}
          <div className="lg:col-span-4 space-y-4">
            <h5 className="font-mono-tech text-[10px] text-primary uppercase tracking-widest">
              Herramientas de Trazado
            </h5>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setLayoutTool("wall")}
                className={`py-2 px-3 text-[10px] font-mono-tech uppercase rounded border cursor-pointer transition-all ${
                  layoutTool === "wall"
                    ? "bg-primary border-primary text-primary-foreground font-bold"
                    : "bg-void border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                🧱 Muros (Paredes)
              </button>
              <button
                onClick={() => setLayoutTool("electric")}
                className={`py-2 px-3 text-[10px] font-mono-tech uppercase rounded border cursor-pointer transition-all ${
                  layoutTool === "electric"
                    ? "bg-primary border-primary text-primary-foreground font-bold"
                    : "bg-void border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                ⚡ Red Eléctrica
              </button>
              <button
                onClick={() => setLayoutTool("plumbing")}
                className={`py-2 px-3 text-[10px] font-mono-tech uppercase rounded border cursor-pointer transition-all ${
                  layoutTool === "plumbing"
                    ? "bg-primary border-primary text-primary-foreground font-bold"
                    : "bg-void border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                💧 Red Hidráulica
              </button>
              <button
                onClick={() => setLayoutTool("furniture")}
                className={`py-2 px-3 text-[10px] font-mono-tech uppercase rounded border cursor-pointer transition-all ${
                  layoutTool === "furniture"
                    ? "bg-primary border-primary text-primary-foreground font-bold"
                    : "bg-void border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                🛋️ Mobiliario
              </button>
            </div>

            {/* Furniture dropdown selector */}
            {layoutTool === "furniture" && (
              <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-200">
                <label className="text-[9px] font-mono-tech text-muted-foreground uppercase block">Objeto a colocar:</label>
                <select
                  value={selectedFurniture}
                  onChange={(e) => setSelectedFurniture(e.target.value)}
                  className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                >
                  <option value="Sofa">🛋️ Sofá</option>
                  <option value="Bed">🛏️ Cama Doble</option>
                  <option value="Table">🪑 Mesa de Comedor</option>
                  <option value="Sink">🚿 Lavabo/Ducha</option>
                  <option value="Door">🚪 Puerta</option>
                </select>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2 pt-2">
              <span className="font-mono-tech text-[8px] text-muted-foreground uppercase tracking-widest block">Controles del Plano</span>
              <div className="flex gap-2">
                <button
                  onClick={handleUndo}
                  disabled={elements.length === 0}
                  className="flex-1 py-1.5 border border-border hover:border-primary text-foreground hover:bg-card text-[9px] font-mono-tech uppercase rounded cursor-pointer disabled:opacity-50 transition-all flex items-center justify-center gap-1"
                >
                  <Undo2 className="w-3 h-3" /> Deshacer
                </button>
                <button
                  onClick={handleClear}
                  disabled={elements.length === 0}
                  className="flex-1 py-1.5 border border-border hover:border-destructive text-foreground hover:bg-destructive/10 text-[9px] font-mono-tech uppercase rounded cursor-pointer disabled:opacity-50 transition-all flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Limpiar
                </button>
              </div>
              <button
                onClick={handleExportSVG}
                disabled={elements.length === 0}
                className="w-full py-2 bg-void border border-primary hover:bg-primary/5 text-primary text-[10px] font-mono-tech uppercase tracking-widest rounded cursor-pointer disabled:opacity-50 transition-all flex items-center justify-center gap-1.5 font-bold"
              >
                <Download className="w-3.5 h-3.5" /> Exportar Plano SVG
              </button>
            </div>

            <div className="bg-primary/5 border border-primary/10 p-3 rounded text-[11px] text-muted-foreground leading-relaxed">
              <span className="font-mono-tech text-[9px] font-bold text-primary block uppercase mb-1">
                Autocotas de Rayon Design:
              </span>
              Cada elemento lineal que traces mostrará automáticamente sus cotas físicas métricas calculadas en tiempo real en su punto medio.
            </div>
          </div>

          {/* Right panel: 2D Layout Design area */}
          <div className="lg:col-span-8 flex flex-col space-y-2">
            <div className="relative border border-border bg-void rounded-lg overflow-hidden h-[300px] w-full select-none cursor-crosshair">
              {/* Grid backgrounds */}
              <div className="absolute inset-0 grid-blueprint opacity-20 pointer-events-none" />

              <svg
                ref={canvasRef}
                className="w-full h-full"
                viewBox="0 0 500 300"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
              >
                {/* Placed Elements */}
                {elements.map((elem) => {
                  let midX = 0;
                  let midY = 0;
                  let lenMeters = "0.0";
                  
                  if (elem.x1 !== undefined && elem.y1 !== undefined && elem.x2 !== undefined && elem.y2 !== undefined) {
                    midX = (elem.x1 + elem.x2) / 2;
                    midY = (elem.y1 + elem.y2) / 2;
                    const pxLen = Math.sqrt(Math.pow(elem.x2 - elem.x1, 2) + Math.pow(elem.y2 - elem.y1, 2));
                    lenMeters = (pxLen / 50).toFixed(1); // 50px = 1 meter scale
                  }

                  return (
                    <g key={elem.id}>
                      {elem.type === "wall" && (
                        <line
                          x1={elem.x1}
                          y1={elem.y1}
                          x2={elem.x2}
                          y2={elem.y2}
                          stroke="#b0b0b0"
                          strokeWidth="5"
                          strokeLinecap="round"
                        />
                      )}
                      {elem.type === "electric" && (
                        <line
                          x1={elem.x1}
                          y1={elem.y1}
                          x2={elem.x2}
                          y2={elem.y2}
                          stroke="#FFD700"
                          strokeWidth="2.5"
                          strokeDasharray="4,3"
                          strokeLinecap="round"
                        />
                      )}
                      {elem.type === "plumbing" && (
                        <line
                          x1={elem.x1}
                          y1={elem.y1}
                          x2={elem.x2}
                          y2={elem.y2}
                          stroke="#00F5FF"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                      )}
                      
                      {/* Metrical auto dimension tag */}
                      {elem.type !== "furniture" && (
                        <g>
                          <rect
                            x={midX - 16}
                            y={midY - 7}
                            width="32"
                            height="14"
                            rx="2"
                            fill="rgba(15, 23, 42, 0.85)"
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="0.5"
                          />
                          <text
                            x={midX}
                            y={midY + 3}
                            fill="#39FF14"
                            fontSize="8"
                            textAnchor="middle"
                            fontFamily="monospace"
                            fontWeight="bold"
                          >
                            {lenMeters}m
                          </text>
                        </g>
                      )}

                      {elem.type === "furniture" && elem.x !== undefined && elem.y !== undefined && (
                        <g transform={`translate(${elem.x - 15}, ${elem.y - 15})`}>
                          <rect width="30" height="30" rx="3" fill="rgba(57,255,20,0.15)" stroke="#39FF14" strokeWidth="1" />
                          <text x="15" y="18" fill="#39FF14" fontSize="8" textAnchor="middle" fontFamily="monospace">
                            {elem.name === "Sofa" ? "🛋️" : elem.name === "Bed" ? "🛏️" : elem.name === "Table" ? "🪑" : elem.name === "Sink" ? "🚿" : "🚪"}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}

                {/* Temporary drawing line preview with real-time dimension */}
                {tempDrawing && (
                  <g>
                    <line
                      x1={tempDrawing.x1}
                      y1={tempDrawing.y1}
                      x2={tempDrawing.x2}
                      y2={tempDrawing.y2}
                      stroke={layoutTool === "wall" ? "#ffffff" : layoutTool === "electric" ? "#FFD700" : "#00F5FF"}
                      strokeWidth={layoutTool === "wall" ? "4" : "2"}
                      strokeDasharray={layoutTool === "electric" ? "4,3" : undefined}
                      opacity="0.6"
                    />
                    <g>
                      <rect
                        x={((tempDrawing.x1 + tempDrawing.x2) / 2) - 16}
                        y={((tempDrawing.y1 + tempDrawing.y2) / 2) - 7}
                        width="32"
                        height="14"
                        rx="2"
                        fill="rgba(15, 23, 42, 0.9)"
                        stroke="#ffffff"
                        strokeWidth="0.5"
                      />
                      <text
                        x={(tempDrawing.x1 + tempDrawing.x2) / 2}
                        y={((tempDrawing.y1 + tempDrawing.y2) / 2) + 3}
                        fill="#ffffff"
                        fontSize="8"
                        textAnchor="middle"
                        fontFamily="monospace"
                      >
                        {(Math.sqrt(Math.pow(tempDrawing.x2 - tempDrawing.x1, 2) + Math.pow(tempDrawing.y2 - tempDrawing.y1, 2)) / 50).toFixed(1)}m
                      </text>
                    </g>
                  </g>
                )}
              </svg>

              <div className="absolute top-2 left-2 font-mono-tech text-[8px] text-muted-foreground uppercase bg-void/80 p-1 border border-border rounded">
                ESQUEMA ARQUITECTÓNICO // ESCALA 1:50 // Snapping 10px
              </div>
            </div>
          </div>
        </div>
      ) : activeSubTab === "calc" ? (
        /* 2. Calculadora Estructural */
        <div className="space-y-6 pt-2">
          {/* Sub-tab selection */}
          <div className="flex bg-void p-1 border border-border rounded w-fit flex-wrap gap-1">
            {(["brick", "concrete", "pipe", "tile", "water"] as const).map((type) => (
              <button
                key={type}
                onClick={() => setCalcType(type)}
                className={`px-3 py-1.5 text-[9px] font-mono-tech uppercase rounded cursor-pointer transition-all ${
                  calcType === type ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {type === "brick" ? "Muros/Bloques" : type === "concrete" ? "Mezclas de Cemento" : type === "pipe" ? "Tuberías y Cableado" : type === "tile" ? "Lozas y Baldosas" : "Cisterna y Agua"}
              </button>
            ))}
          </div>

          {calcType === "brick" ? (
            <div className="grid md:grid-cols-2 gap-6 animate-in fade-in duration-200">
              {/* Cement block inputs */}
              <div className="space-y-4">
                <h5 className="font-mono-tech text-[10px] text-primary uppercase tracking-widest">
                  Parámetros de Muro de Bloques de Hormigón
                </h5>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                    <span>Largo del Muro:</span>
                    <span className="text-primary font-bold">{wallLength} m ({(wallLength * 3.28084).toFixed(1)} ft)</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={wallLength}
                    onChange={(e) => setWallLength(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                    <span>Alto del Muro:</span>
                    <span className="text-primary font-bold">{wallHeight} m ({(wallHeight * 3.28084).toFixed(1)} ft)</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={wallHeight}
                    onChange={(e) => setWallHeight(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Espesor Bloque:</label>
                    <select
                      value={blockThickness}
                      onChange={(e) => setBlockThickness(e.target.value)}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                    >
                      <option value="4" className="bg-background">4" (Divisiones)</option>
                      <option value="6" className="bg-background">6" (Pared Estándar)</option>
                      <option value="8" className="bg-background">8" (De Carga)</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Varillas (Celdas):</label>
                    <select
                      value={rebarSpacing}
                      onChange={(e) => setRebarSpacing(Number(e.target.value))}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                    >
                      <option value={16} className="bg-background">Cada 16" (2 celdas)</option>
                      <option value={24} className="bg-background">Cada 24" (3 celdas)</option>
                      <option value={32} className="bg-background">Cada 32" (4 celdas)</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Calibre Varilla:</label>
                    <select
                      value={rebarSize}
                      onChange={(e) => setRebarSize(e.target.value)}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                    >
                      <option value="3/8" className="bg-background">#3 (3/8")</option>
                      <option value="1/2" className="bg-background">#4 (1/2")</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Outputs */}
              <div className="bg-void/50 border border-border/80 p-6 rounded-lg flex flex-col justify-between">
                <div>
                  <span className="font-mono-tech text-[9px] uppercase tracking-widest text-primary border-b border-border/20 pb-2 block">
                    Materiales Estimados (Norma PR - Huracanes)
                  </span>

                  <div className="grid grid-cols-2 gap-4 py-3">
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Bloques Necesarios (8"x16")</span>
                      <div className="text-sm font-bold text-foreground">{totalBlocksNeeded} bloques</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Área: {wallAreaSqM} m² (${estimatedBlocksCost.toFixed(2)})</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Varillas de Acero Grado 60</span>
                      <div className="text-sm font-bold text-foreground">{totalRebarRods20ft} varillas (20ft)</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">V: {rebarColsCount} uds | H: {horizontalRebarRows} filas (${estimatedRebarCost.toFixed(2)})</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Alambre de Amarre #16</span>
                      <div className="text-sm font-bold text-foreground">{tieWireLbs} lbs</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">{rebarJunctions} uniones en malla (${estimatedTieWireCost.toFixed(2)})</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Cemento Portland Requerido</span>
                      <div className="text-sm font-bold text-foreground">{wallCementBags} sacos (42.5kg)</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Mortero + Celdas rellenas (${estimatedCementCost.toFixed(2)})</span>
                    </div>
                  </div>

                  <div className="border-t border-border/20 pt-2 pb-2 flex justify-between items-center text-[10px] font-mono-tech">
                    <span className="text-muted-foreground uppercase">Costo Estimado de Materiales (PR):</span>
                    <strong className="text-[#39FF14] text-xs font-bold">${totalCalculatedWallCost.toFixed(2)}</strong>
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/10 p-2.5 rounded text-[8px] text-muted-foreground leading-normal space-y-1">
                  <span className="font-mono-tech text-[8px] font-bold text-primary block uppercase">Normativa ACI-318 & ASCE 7 (PR):</span>
                  • **Protección contra Salitre**: En zonas costeras de la isla, mantén un recubrimiento de concreto mínimo de 2" sobre las varillas para evitar que el aire marino penetre y cause corrosión interna.<br />
                  • **Resistencia categoría 5**: Asegura que las varillas verticales penetren la zapata y que se instalen bloques de 6" u 8" rellenos en su totalidad con concreto líquido (grout).
                </div>
              </div>
            </div>
          ) : calcType === "concrete" ? (
            <div className="grid md:grid-cols-2 gap-6 animate-in fade-in duration-200">
              {/* Concrete inputs */}
              <div className="space-y-4">
                <h5 className="font-mono-tech text-[10px] text-primary uppercase tracking-widest">
                  Parámetros de la Losa / Mezcla de Cemento
                </h5>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Tipo de Dosificación:</label>
                  <select
                    value={concreteMixType}
                    onChange={(e) => setConcreteMixType(e.target.value as any)}
                    className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                  >
                    <option value="structural" className="bg-background">Hormigón Estructural 1:2:3 (Vigas/Zapatas/Losas)</option>
                    <option value="mortar" className="bg-background">Mortero de Empañete / Pega de Bloques 1:3</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                    <span>Largo de Losa:</span>
                    <span className="text-primary font-bold">{slabLength} m ({(slabLength * 3.28084).toFixed(1)} ft)</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    value={slabLength}
                    onChange={(e) => setSlabLength(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                    <span>Ancho de Losa:</span>
                    <span className="text-primary font-bold">{slabWidth} m ({(slabWidth * 3.28084).toFixed(1)} ft)</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    value={slabWidth}
                    onChange={(e) => setSlabWidth(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                    <span>Espesor de Mezcla (Grosor):</span>
                    <span className="text-primary font-bold">{Math.round(slabThickness * 100)} cm ({(slabThickness * 39.3701).toFixed(1)} in)</span>
                  </div>
                  <input
                    type="range"
                    min="0.05"
                    max="0.4"
                    step="0.01"
                    value={slabThickness}
                    onChange={(e) => setSlabThickness(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                  />
                </div>
              </div>

              {/* Concrete outputs */}
              <div className="bg-void/50 border border-border/80 p-6 rounded-lg flex flex-col justify-between">
                <div>
                  <span className="font-mono-tech text-[9px] uppercase tracking-widest text-primary border-b border-border/20 pb-2 block">
                    Materiales en Sacos Minoristas (Ferretería PR)
                  </span>

                  <div className="grid grid-cols-2 gap-4 py-3">
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Volumen Total</span>
                      <div className="text-xl font-bold text-foreground">{slabVolumeCubicM} m³</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">{volYards.toFixed(2)} yd³ (yardas de mezcla)</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Sacos de Cemento Gris (47 lbs)</span>
                      <div className="text-xl font-bold text-foreground">{cementBags47lb} sacos</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Sacos estándar de 47 lbs</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Sacos de Arena Limpia (50 lbs)</span>
                      <div className="text-lg font-bold text-foreground">{sandBags50lb} sacos</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Bolsas de arena para mezcla</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Sacos de Piedra/Grava (50 lbs)</span>
                      <div className="text-lg font-bold text-foreground">{concreteMixType === "mortar" ? "No requiere" : `${gravelBags50lb} sacos`}</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">{concreteMixType === "mortar" ? "Mortero fino" : "Piedra de 1/2 pulgada"}</span>
                    </div>
                    <div className="space-y-0.5 col-span-2">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Agua Limpia Requerida</span>
                      <div className="text-md font-bold text-[#00F5FF]">{waterGalsNeeded} galones</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Aproximado para la consistencia estándar</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 pt-2 text-left">
                  <div className="bg-primary/5 border border-primary/10 p-2.5 rounded text-[10px] text-muted-foreground leading-normal">
                    <span className="font-mono-tech text-[9px] font-bold text-primary block uppercase mb-0.5">Proporción Utilizada:</span>
                    {concreteMixType === "structural" 
                      ? "Proporción 1:2:3 (1 cemento gris, 2 arena, 3 grava de 1/2 pulgada). Brinda resistencia de 3,000 PSI, ideal para zapatas portantes y techos." 
                      : "Proporción 1:3 (1 cemento, 3 arena limpia). Sin piedra. Ideal para empañetar paredes o pegar bloques de hormigón."}
                  </div>

                  {/* Hurricane & Salitre Resilience HUD Panel (110% PR Upgrade) */}
                  <div className="bg-void border border-border/80 p-3 rounded space-y-2.5">
                    <div className="flex justify-between items-center border-b border-border/30 pb-1.5">
                      <span className="font-mono-tech text-[8px] text-[#39FF14] uppercase tracking-widest font-bold">🛡️ Reporte de Resiliencia Huracán Cat 5 (ASCE 7)</span>
                      <span className="text-[7.5px] bg-[#39FF14]/20 text-[#39FF14] px-1 rounded font-bold uppercase">PR Standard</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-[9px] font-mono-tech text-muted-foreground">
                      <div className="space-y-0.5">
                        <span className="text-[7px] text-muted-foreground uppercase block">Resistencia Mecánica:</span>
                        <strong className={concreteMixType === "structural" ? "text-[#39FF14]" : "text-amber-400"}>
                          {concreteMixType === "structural" ? "3,000 PSI (Estructural)" : "2,000 PSI (Mortero Pega)"}
                        </strong>
                      </div>
                      <div className="space-y-0.5">
                        <span className="text-[7px] text-muted-foreground uppercase block">Resistencia Vuelco (175mph):</span>
                        <strong className={slabThickness >= 0.12 ? "text-[#39FF14]" : "text-amber-400"}>
                          {slabThickness >= 0.12 ? "Seguro (Losa Pesada)" : "Riesgo (Losa Ligera)"}
                        </strong>
                      </div>
                      <div className="space-y-0.5 col-span-2">
                        <span className="text-[7px] text-muted-foreground uppercase block">Normativa de Varilla vertical en Puerto Rico:</span>
                        <span className="text-foreground leading-normal block text-[8px]">
                          {rebarSpacing <= 16 
                            ? "✅ Óptimo: Distribución de varilla #4 cada 16\" anclada a zapata cumple con ASCE 7."
                            : "⚠️ Alerta: Espaciamiento sugerido de 16\" en lugar de " + rebarSpacing + "\" para muros expuestos."}
                        </span>
                      </div>
                    </div>

                    <div className="text-[7.5px] text-muted-foreground leading-normal border-t border-border/20 pt-2 space-y-1">
                      <p>🌊 <strong>Prevención de Salitre:</strong> Usar varilla con recubrimiento epóxico (ASTM A775) o galvanizado si construyes a menos de 5 millas de la costa.</p>
                      <p>☀️ <strong>Curado en Clima Tropical:</strong> Rociar la losa con agua 3 veces al día durante 7 días para evitar agrietamiento por evaporación solar rápida.</p>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      const printHtml = `<!DOCTYPE html><html><head><title>Ficha de Compra Concreto — Prometheus</title>
<style>
  body { font-family: sans-serif; background: white; color: #111; padding: 30px; max-width: 600px; margin: 0 auto; }
  h1 { font-size: 20px; color: #c04000; border-bottom: 2px solid #c04000; padding-bottom: 6px; }
  table { width: 100%; border-collapse: collapse; margin-top: 15px; }
  th { text-align: left; background: #f3f4f6; padding: 8px; border: 1px solid #ddd; }
  td { padding: 8px; border: 1px solid #ddd; }
  .note { background: #fef3c7; border-left: 4px solid #d97706; padding: 10px; margin-top: 20px; font-size: 12px; }
  .suppliers { margin-top: 20px; font-size: 11px; color: #555; }
</style></head><body>
  <h1>Ficha de Compra y Dosificación de Concreto</h1>
  <p><strong>Proyecto:</strong> Mezcla de Hormigón / Mortero</p>
  <p><strong>Dimensiones:</strong> Losa de ${slabLength}m x ${slabWidth}m x ${Math.round(slabThickness * 100)}cm (Volumen: ${slabVolumeCubicM} m³)</p>
  <p><strong>Tipo:</strong> ${concreteMixType === "structural" ? "Hormigón Estructural 1:2:3" : "Mortero de Revoque 1:3"}</p>
  <table>
    <thead><tr><th>Material</th><th>Cantidad Requerida</th><th>Especificaciones</th></tr></thead>
    <tbody>
      <tr><td>Cemento Gris</td><td><strong>${cementBags47lb} sacos</strong></td><td>Sacos de 47 lbs</td></tr>
      <tr><td>Arena de Río</td><td><strong>${sandBags50lb} bolsas</strong></td><td>Bolsas de 50 lbs</td></tr>
      ${concreteMixType === "structural" ? `<tr><td>Piedra/Grava</td><td><strong>${gravelBags50lb} bolsas</strong></td><td>Bolsas de 50 lbs (Grava 1/2")</td></tr>` : ""}
      <tr><td>Agua Requerida</td><td><strong>${waterGalsNeeded} galones</strong></td><td>Agua limpia para curado</td></tr>
    </tbody>
  </table>
  <div class="note"><strong>Instrucción Maker:</strong> Mezcla primero en seco el cemento y la arena. Añade la piedra y luego vierte el agua lentamente. Cura el concreto humedeciéndolo con agua dos veces al día por 7 días después del vaciado.</div>
  <div class="suppliers"><strong>Canales de Abastecimiento recomendados en Puerto Rico:</strong> National Lumber & Hardware, Ferreterías Ace, Home Depot, Maderas 3C.</div>
</body></html>`;
                      const w = window.open("", "_blank");
                      if (w) {
                        w.document.write(printHtml);
                        w.document.close();
                        setTimeout(() => w.print(), 400);
                      }
                    }}
                    className="w-full flex items-center justify-center gap-1.5 py-1.5 border border-primary/40 hover:border-primary text-primary hover:text-foreground hover:bg-primary/10 rounded font-mono-tech text-[9px] uppercase tracking-wider transition-all cursor-pointer"
                  >
                    <span>Imprimir Ficha de Compra (BOM)</span>
                  </button>
                </div>
              </div>
            </div>
          ) : calcType === "pipe" ? (
            <div className="grid md:grid-cols-2 gap-6 animate-in fade-in duration-200">
              {/* Pipe inputs */}
              <div className="space-y-4">
                <h5 className="font-mono-tech text-[10px] text-primary uppercase tracking-widest">
                  Parámetros de Tubería / Red Hidráulica
                </h5>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                    <span>Longitud de Ruta:</span>
                    <span className="text-primary font-bold">{lineLength} m ({(lineLength * 3.28084).toFixed(1)} ft)</span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="200"
                    step="5"
                    value={lineLength}
                    onChange={(e) => setLineLength(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Diámetro Nominal (Pulgadas):</label>
                    <select
                      value={plumbPipeDiameter}
                      onChange={(e) => setPlumbPipeDiameter(Number(e.target.value))}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                    >
                      <option value="0.5" className="bg-background">1/2" (Flujo bajo/Grifos)</option>
                      <option value="0.75" className="bg-background">3/4" (Línea principal/Cisterna)</option>
                      <option value="1.0" className="bg-background">1" (Succión / Caudal alto)</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Potencia Bomba (HP):</label>
                    <select
                      value={plumbPumpHP}
                      onChange={(e) => setPlumbPumpHP(Number(e.target.value))}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                    >
                      <option value="0.5" className="bg-background">1/2 HP (Típico residencial)</option>
                      <option value="0.75" className="bg-background">3/4 HP (Presión media)</option>
                      <option value="1.0" className="bg-background">1 HP (Alta demanda)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Pipe outputs */}
              <div className="bg-void/50 border border-border/80 p-6 rounded-lg flex flex-col justify-between">
                <div>
                  <span className="font-mono-tech text-[9px] uppercase tracking-widest text-primary border-b border-border/20 pb-2 block">
                    Cálculo Hidráulico & Fricción
                  </span>

                  <div className="grid grid-cols-2 gap-4 py-3">
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Tramos Requeridos (3m / 10ft)</span>
                      <div className="text-xl font-bold text-foreground">{total3mPipes} tubos</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Longitud total: {(lineLength * 3.28084).toFixed(1)} ft</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Caudal de la Bomba</span>
                      <div className="text-xl font-bold text-[#00F5FF]">{flowGPM} GPM</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">{flowLPM.toFixed(1)} Litros/min</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Velocidad del Agua</span>
                      <div className="text-lg font-bold text-foreground">{waterVelocityFps.toFixed(2)} ft/s</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Límite recomendado: 5.0 ft/s</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Pérdida por Fricción (Caída)</span>
                      <div className="text-lg font-bold text-red-400">-{frictionLossPSI.toFixed(2)} PSI</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Pérdida en tubería de plástico</span>
                    </div>
                    <div className="space-y-0.5 col-span-2 border-t border-border/20 pt-2">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Presión Dinámica Restante</span>
                      <div className={`text-xl font-bold ${pipePressurePSI < 20 ? "text-red-500 font-bold" : "text-[#39FF14]"}`}>
                        {pipePressurePSI.toFixed(1)} PSI
                      </div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">
                        {pipePressurePSI < 20 
                          ? "⚠ Presión muy baja. Aumenta el diámetro a 3/4\" o 1\" para reducir pérdidas por fricción." 
                          : "✓ Presión adecuada para grifos residenciales."}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/10 p-3 rounded text-[10px] text-muted-foreground">
                  <span className="font-mono-tech text-[9px] font-bold text-primary block uppercase mb-0.5">Fórmula de Hazen-Williams:</span>
                  Calcula cómo el rozamiento del agua contra las paredes internas del tubo reduce la presión. Usar tuberías más delgadas (1/2") aumenta la pérdida de presión exponencialmente ante caudales altos.
                </div>
              </div>
            </div>
          ) : calcType === "tile" ? (
            <div className="grid md:grid-cols-2 gap-6 animate-in fade-in duration-200">
              {/* Tile inputs */}
              <div className="space-y-4">
                <h5 className="font-mono-tech text-[10px] text-primary uppercase tracking-widest">
                  Parámetros de Colocación de Lozas / Azulejos
                </h5>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                      <span>Largo del Área:</span>
                      <span className="text-primary font-bold">{tileAreaLength} m ({(tileAreaLength * 3.28084).toFixed(1)} ft)</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      step="0.5"
                      value={tileAreaLength}
                      onChange={(e) => setTileAreaLength(Number(e.target.value))}
                      className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                      <span>Ancho del Área:</span>
                      <span className="text-primary font-bold">{tileAreaWidth} m ({(tileAreaWidth * 3.28084).toFixed(1)} ft)</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      step="0.5"
                      value={tileAreaWidth}
                      onChange={(e) => setTileAreaWidth(Number(e.target.value))}
                      className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Formato / Preset:</label>
                    <select
                      value={tilePreset}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTilePreset(val);
                        if (val === "30x30") {
                          setTileWidthCm(30);
                          setTileHeightCm(30);
                        } else if (val === "45x45") {
                          setTileWidthCm(45);
                          setTileHeightCm(45);
                        } else if (val === "60x60") {
                          setTileWidthCm(60);
                          setTileHeightCm(60);
                        } else if (val === "30x60") {
                          setTileWidthCm(30);
                          setTileHeightCm(60);
                        }
                      }}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                    >
                      <option value="30x30" className="bg-background">30 x 30 cm (12"x12" Estándar)</option>
                      <option value="45x45" className="bg-background">45 x 45 cm (18"x18" Mediana)</option>
                      <option value="60x60" className="bg-background">60 x 60 cm (24"x24" Porcelanato)</option>
                      <option value="30x60" className="bg-background">30 x 60 cm (12"x24" Rectangular)</option>
                      <option value="custom" className="bg-background">Personalizado</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Patrón de Colocación:</label>
                    <select
                      value={tilePattern}
                      onChange={(e) => setTilePattern(e.target.value)}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                    >
                      <option value="orthogonal" className="bg-background">Ortogonal / Regular (Al Hilo)</option>
                      <option value="offset" className="bg-background">Trabado (Offset 50% / Matajunta)</option>
                      <option value="diagonal" className="bg-background">Diagonal (45° - requiere + merma)</option>
                    </select>
                  </div>
                </div>

                {tilePreset === "custom" && (
                  <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-200">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Ancho de Loza (cm):</label>
                      <input
                        type="number"
                        min="5"
                        max="150"
                        value={tileWidthCm}
                        onChange={(e) => setTileWidthCm(Math.max(1, Number(e.target.value)))}
                        className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Largo de Loza (cm):</label>
                      <input
                        type="number"
                        min="5"
                        max="150"
                        value={tileHeightCm}
                        onChange={(e) => setTileHeightCm(Math.max(1, Number(e.target.value)))}
                        className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Separación Sisa (mm):</label>
                    <input
                      type="number"
                      min="0"
                      max="15"
                      value={tileJointMm}
                      onChange={(e) => setTileJointMm(Number(e.target.value))}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Margen Merma (%):</label>
                    <input
                      type="number"
                      min="5"
                      max="30"
                      value={tileWastePercent}
                      onChange={(e) => setTileWastePercent(Number(e.target.value))}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Costo por m² ($):</label>
                    <input
                      type="number"
                      min="1"
                      value={tileCostPerSqM}
                      onChange={(e) => setTileCostPerSqM(Number(e.target.value))}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Tile outputs */}
              <div className="bg-void/50 border border-border/80 p-6 rounded-lg flex flex-col justify-between">
                <div>
                  <span className="font-mono-tech text-[9px] uppercase tracking-widest text-primary border-b border-border/20 pb-2 block">
                    Materiales de Colocación Estimados
                  </span>

                  <div className="grid grid-cols-2 gap-4 py-4">
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Área de Superficie</span>
                      <div className="text-xl font-bold text-foreground">{tileAreaSqM} m²</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">{(tileAreaSqM * 10.7639).toFixed(1)} sq ft (pies cuadrados)</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Piezas Requeridas (Lozas)</span>
                      <div className="text-xl font-bold text-foreground">{totalTilesNeeded} unidades</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">
                        Con {actualWastePercent}% de merma ({tilePattern === "diagonal" ? "Diagonal" : "Recto"})
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Cajas Necesarias</span>
                      <div className="text-xl font-bold text-foreground">{totalTileBoxes} cajas</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">
                        Caja: {coveragePerBox} m² ({(coveragePerBox * 10.7639).toFixed(1)} sq ft)
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Presupuesto Estimado</span>
                      <div className="text-xl font-bold text-foreground">${totalTileCostEstimate.toFixed(2)} USD</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Costo material con desperdicio</span>
                    </div>
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/10 p-3.5 rounded text-[10px] text-muted-foreground leading-normal space-y-1.5 mt-2">
                  <span className="font-mono-tech text-[9px] font-bold text-primary block uppercase">Recomendaciones del Taller (Puerto Rico):</span>
                  <p><strong>🇵🇷 Pega y Humedad:</strong> Utiliza una pega tipo ANSI A118.4 o superior (modificada con polímeros). La alta humedad relativa y oscilaciones térmicas del trópico pueden hacer que las baldosas se "soplen" o despeguen si usas mezclas cementicias básicas.</p>
                  <p><strong>🔧 Margen de Corte:</strong> Para colocación diagonal se exige mínimo 15% de merma debido a los numerosos cortes triangulares en las esquinas. Si el espacio tiene muchas columnas o mochetas, añade 5% extra a tu cálculo.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6 animate-in fade-in duration-200">
              {/* Rainwater collection inputs */}
              <div className="space-y-4">
                <h5 className="font-mono-tech text-[10px] text-primary uppercase tracking-widest">
                  Parámetros de Captación de Agua y Cisterna
                </h5>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                      <span>Largo del Techo:</span>
                      <span className="text-primary font-bold">{roofLength} m ({(roofLength * 3.28084).toFixed(1)} ft)</span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="30"
                      step="0.5"
                      value={roofLength}
                      onChange={(e) => setRoofLength(Number(e.target.value))}
                      className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                      <span>Ancho del Techo:</span>
                      <span className="text-primary font-bold">{roofWidth} m ({(roofWidth * 3.28084).toFixed(1)} ft)</span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="30"
                      step="0.5"
                      value={roofWidth}
                      onChange={(e) => setRoofWidth(Number(e.target.value))}
                      className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                    <span>Lluvia Anual Promedio:</span>
                    <span className="text-primary font-bold">{annualRainfall} mm ({annualRainfallInches} in)</span>
                  </div>
                  <input
                    type="range"
                    min="500"
                    max="3000"
                    step="50"
                    value={annualRainfall}
                    onChange={(e) => setAnnualRainfall(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-[8px] text-muted-foreground font-mono-tech">
                    <span>PR Sur (Poco húmedo): ~800mm</span>
                    <span>PR Norte/Sistemas: ~1650mm</span>
                    <span>El Yunque (Bosque Lluvioso): ~3000mm</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Capacidad Cisterna (Galones):</label>
                    <input
                      type="number"
                      min="100"
                      max="10000"
                      step="100"
                      value={cisternCapacity}
                      onChange={(e) => setCisternCapacity(Math.max(1, Number(e.target.value)))}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono-tech text-muted-foreground uppercase block">Consumo Diario del Hogar (Gal):</label>
                    <input
                      type="number"
                      min="10"
                      max="1000"
                      step="10"
                      value={dailyConsumption}
                      onChange={(e) => setDailyConsumption(Math.max(1, Number(e.target.value)))}
                      className="w-full bg-void border border-border text-xs text-foreground p-2 rounded focus:outline-none focus:border-primary/50"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                    <span>Eficiencia de Recolección:</span>
                    <span className="text-primary font-bold">{rainHarvestEfficiency}%</span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="100"
                    step="5"
                    value={rainHarvestEfficiency}
                    onChange={(e) => setRainHarvestEfficiency(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
                  />
                </div>

                <div className="space-y-2 border-t border-border/20 pt-2">
                  <span className="font-mono-tech text-[9px] text-foreground uppercase block font-bold">Química y Calidad del Agua</span>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="font-mono-tech text-[8px] text-muted-foreground uppercase flex justify-between">
                        <span>pH del Agua:</span>
                        <span className="text-primary font-bold">{pHValue.toFixed(1)}</span>
                      </label>
                      <input
                        type="range"
                        min="5.0"
                        max="9.0"
                        step="0.1"
                        value={pHValue}
                        onChange={(e) => setPHValue(parseFloat(e.target.value))}
                        className="w-full accent-primary"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="font-mono-tech text-[8px] text-muted-foreground uppercase block">TDS (Sólidos Disueltos - PPM):</label>
                      <input
                        type="number"
                        min="10"
                        max="1200"
                        value={tdsPPM}
                        onChange={(e) => setTdsPPM(Math.max(0, Number(e.target.value)))}
                        className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Rainwater outputs */}
              <div className="bg-void/50 border border-border/80 p-6 rounded-lg flex flex-col justify-between">
                <div>
                  <span className="font-mono-tech text-[9px] uppercase tracking-widest text-primary border-b border-border/20 pb-2 block">
                    Cisternas & Potabilización Química
                  </span>

                  <div className="grid grid-cols-2 gap-4 py-3">
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Área del Techo</span>
                      <div className="text-xl font-bold text-foreground">{roofAreaSqM} m²</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">{roofAreaSqFt} sq ft</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Captación Anual Máxima</span>
                      <div className="text-xl font-bold text-foreground">{harvestPotentialGallons.toLocaleString()} Gal</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">{harvestPotentialLiters.toLocaleString()} L al año</span>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Autonomía Hídrica</span>
                      <div className="text-xl font-bold text-[#39FF14]">{autonomyDays} días</div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">Con cisterna llena</span>
                    </div>
                    <div className="space-y-0.5 border-r border-border/20 pr-2">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Dosis Desinfectante Cloro</span>
                      <div className="text-lg font-bold text-[#00F5FF]">
                        {Math.ceil(cisternCapacity * 8)} gotas
                      </div>
                      <span className="text-[7px] text-muted-foreground font-mono-tech">
                        ~{Math.ceil((cisternCapacity * 8) / 20)} ml de cloro al 5.25%
                      </span>
                    </div>
                    <div className="space-y-0.5 col-span-2 border-t border-border/20 pt-2">
                      <span className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Diagnóstico de Calidad del Agua</span>
                      <div className="text-xs font-semibold text-foreground space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span className={pHValue < 6.5 || pHValue > 8.5 ? "text-amber-500 font-bold" : "text-[#39FF14]"}>
                            {pHValue < 6.5 
                              ? "⚠ pH Ácido: Riesgo de corrosión en cobre de casa." 
                              : pHValue > 8.5 
                                ? "⚠ pH Alcalino: Riesgo de cal y sarro mineral." 
                                : "✓ pH Óptimo: Agua balanceada (6.5 - 8.5)."}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className={tdsPPM > 500 ? "text-amber-500 font-bold" : "text-[#39FF14]"}>
                            {tdsPPM > 500 
                              ? "⚠ TDS Alto (>500 PPM): Alta salinidad. Requiere Ósmosis Inversa." 
                              : "✓ TDS Óptimo (<500 PPM): Mineralización aceptable para beber."}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/10 p-3 rounded text-[10px] text-muted-foreground leading-normal space-y-1.5 mt-2">
                  <span className="font-mono-tech text-[9px] font-bold text-primary block uppercase">Tratamiento Pluvial (Puerto Rico):</span>
                  <p><strong>🌧️ Desinfectar Cisterna:</strong> Aplica 8 gotas de cloro regular sin aroma por cada galón de agua. Deja reposar 30 minutos antes de consumir.</p>
                  <p><strong>🦠 Lámparas UV:</strong> Para desinfección continua libre de químicos, instala una lámpara esterilizadora ultravioleta (UV) de **12 a 24 Watts** en la línea principal después del filtro de sedimentos de carbón activado.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : activeSubTab === "truss" ? (
        /* 3. Simulador de Cerchas (Truss Stress Simulator) */
        <div className="grid lg:grid-cols-12 gap-8 pt-2">
          {/* Controls */}
          <div className="lg:col-span-4 space-y-4">
            <h5 className="font-mono-tech text-[10px] text-primary uppercase tracking-widest">
              Controles de Análisis de Carga
            </h5>

            {/* Truss Style */}
            <div className="space-y-1.5">
              <label className="text-[9px] font-mono-tech text-muted-foreground uppercase block">Diseño de la Cercha (Estructura)</label>
              <div className="flex bg-void p-1 border border-border rounded">
                {(["pratt", "howe", "warren"] as const).map((style) => (
                  <button
                    key={style}
                    onClick={() => setTrussStyle(style)}
                    className={`flex-1 py-1.5 text-[9px] font-mono-tech uppercase rounded cursor-pointer transition-all ${
                      trussStyle === style ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {style}
                  </button>
                ))}
              </div>
            </div>

            {/* Load slider */}
            <div className="space-y-1.5">
              <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                <span>Carga Aplicada (Fuerza Central):</span>
                <span className="text-primary font-bold">{appliedLoad} N</span>
              </div>
              <input
                type="range"
                min="100"
                max="2000"
                step="100"
                value={appliedLoad}
                onChange={(e) => setAppliedLoad(Number(e.target.value))}
                className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
              />
            </div>

            {/* Truss Span Width */}
            <div className="space-y-1.5">
              <div className="flex justify-between text-[10px] font-mono-tech text-muted-foreground uppercase">
                <span>Luz de la Estructura (Ancho):</span>
                <span className="text-primary font-bold">{trussSpan} px</span>
              </div>
              <input
                type="range"
                min="200"
                max="380"
                step="20"
                value={trussSpan}
                onChange={(e) => setTrussSpan(Number(e.target.value))}
                className="w-full accent-primary bg-void border border-border rounded h-2 appearance-none cursor-pointer"
              />
            </div>

            {/* Color key */}
            <div className="bg-void/50 border border-border/80 p-4 rounded-lg space-y-2">
              <span className="font-mono-tech text-[8px] text-muted-foreground uppercase tracking-widest block">Código de Esfuerzos</span>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-3.5 h-3 bg-[#FF6B35] rounded-sm shrink-0" />
                <span className="text-muted-foreground">Tensión (Estiramiento)</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-3.5 h-3 bg-[#00F5FF] rounded-sm shrink-0" />
                <span className="text-muted-foreground">Compresión (Aplastamiento)</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-3.5 h-3 bg-[#64748b] rounded-sm shrink-0" />
                <span className="text-muted-foreground">Esfuerzo Neutro</span>
              </div>
            </div>
          </div>

          {/* Visual Simulation Display Area */}
          <div className="lg:col-span-8 flex flex-col space-y-2">
            <div className="relative border border-border bg-void rounded-lg overflow-hidden h-[300px] w-full select-none">
              <div className="absolute inset-0 grid-blueprint opacity-10 pointer-events-none" />

              <svg className="w-full h-full" viewBox="0 0 500 300">
                {/* Support labels */}
                {members.map((member, idx) => {
                  const strokeColor =
                    member.type === "tension"
                      ? "#FF6B35"
                      : member.type === "compression"
                      ? "#00F5FF"
                      : "#64748b";
                  const strokeWidth = member.type === "neutral" ? 1.5 : 2 + (appliedLoad / 1000) * 1.5;

                  return (
                    <line
                      key={idx}
                      x1={member.from.x}
                      y1={member.from.y}
                      x2={member.to.x}
                      y2={member.to.y}
                      stroke={strokeColor}
                      strokeWidth={strokeWidth}
                      strokeLinecap="round"
                    />
                  );
                })}

                {/* Nodes */}
                {Object.entries(nodes).map(([name, node]) => (
                  <circle
                    key={name}
                    cx={node.x}
                    cy={node.y}
                    r="5"
                    fill="#1e293b"
                    stroke="#ffffff"
                    strokeWidth="1.5"
                  />
                ))}

                <polygon points="50,180 40,195 60,195" fill="#64748b" stroke="#ffffff" strokeWidth="1" />
                <circle cx={trussSpan + 60} cy="187" r="4" fill="#64748b" stroke="#ffffff" strokeWidth="1" />
                <line x1={trussSpan + 45} y1="192" x2={trussSpan + 75} y2="192" stroke="#ffffff" strokeWidth="1.5" />

                <g transform={`translate(${trussSpan/2 + 60}, ${100 + deformation - 30})`}>
                  <line x1="0" y1="0" x2="0" y2="25" stroke="#ef4444" strokeWidth="2.5" />
                  <polygon points="0,27 -4,20 4,20" fill="#ef4444" />
                  <text x="8" y="15" fill="#ef4444" fontSize="8" fontFamily="monospace" fontWeight="bold">
                    {appliedLoad} N
                  </text>
                </g>
              </svg>

              <div className="absolute top-2 left-2 font-mono-tech text-[8px] text-muted-foreground uppercase bg-void/80 p-1 border border-border rounded">
                SIMULACIÓN MECÁNICA DE COMPORTAMIENTO ESTRUCTURAL DE CERCHA
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* 4. Topografía 3D (Applitop mock) Section */
        <div className="grid lg:grid-cols-12 gap-8 pt-2">
          {/* Controls */}
          <div className="lg:col-span-4 space-y-4">
            <h5 className="font-mono-tech text-[10px] text-primary uppercase tracking-widest">
              Elevación del Terreno
            </h5>

            {/* Corner sliders */}
            <div className="space-y-3.5 bg-void/40 border border-border/80 p-4 rounded-lg">
              <div className="space-y-1">
                <div className="flex justify-between text-[9px] font-mono-tech text-muted-foreground uppercase">
                  <span>Esquina Noroeste (NW):</span>
                  <span className="text-primary font-bold">{elevNW} m</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="40"
                  value={elevNW}
                  onChange={(e) => setElevNW(Number(e.target.value))}
                  className="w-full accent-primary bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                />
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-[9px] font-mono-tech text-muted-foreground uppercase">
                  <span>Esquina Noreste (NE):</span>
                  <span className="text-primary font-bold">{elevNE} m</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="40"
                  value={elevNE}
                  onChange={(e) => setElevNE(Number(e.target.value))}
                  className="w-full accent-primary bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                />
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-[9px] font-mono-tech text-muted-foreground uppercase">
                  <span>Esquina Suroeste (SW):</span>
                  <span className="text-primary font-bold">{elevSW} m</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="40"
                  value={elevSW}
                  onChange={(e) => setElevSW(Number(e.target.value))}
                  className="w-full accent-primary bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                />
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-[9px] font-mono-tech text-muted-foreground uppercase">
                  <span>Esquina Sureste (SE):</span>
                  <span className="text-primary font-bold">{elevSE} m</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="40"
                  value={elevSE}
                  onChange={(e) => setElevSE(Number(e.target.value))}
                  className="w-full accent-primary bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                />
              </div>
            </div>

            {/* Display mode */}
            <div className="space-y-1.5">
              <label className="text-[9px] font-mono-tech text-muted-foreground uppercase block">Estilo de Representación</label>
              <div className="flex bg-void p-1 border border-border rounded">
                {(["points", "grid", "contours"] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setTopoMode(mode)}
                    className={`flex-1 py-1.5 text-[9px] font-mono-tech uppercase rounded cursor-pointer transition-all ${
                      topoMode === mode ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {mode === "points" ? "Puntos" : mode === "grid" ? "Malla" : "Contornos"}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/10 p-3.5 rounded text-[11px] text-muted-foreground leading-relaxed">
              <span className="font-mono-tech text-[9px] font-bold text-primary block uppercase mb-1">
                Visualización de Terreno:
              </span>
              *Puntos* simula una **Nube de Puntos (PointCloud)** escaneada por láser. *Malla* calcula la interpolación estructural y *Contornos* muestra las isolíneas de altura.
            </div>
          </div>

          {/* Canvas Display */}
          <div className="lg:col-span-8 flex flex-col space-y-2">
            <div className="relative border border-border bg-void rounded-lg overflow-hidden h-[300px] w-full select-none">
              <canvas ref={topoCanvasRef} width={450} height={300} className="w-full h-full" />
              
              <div className="absolute top-2 left-2 font-mono-tech text-[8px] text-[#00F5FF]/60 uppercase tracking-widest bg-void/70 p-1 border border-[#00F5FF]/20 rounded">
                RECONSTRUCCIÓN DE MALLA DE ELEVACIÓN DIGITAL (DEM)
              </div>
              
              <div className="absolute bottom-2 left-2 font-mono-tech text-[8px] text-muted-foreground uppercase">
                Modo: {topoMode === "points" ? "POINT CLOUD (LIDAR MOCK)" : topoMode === "grid" ? "STRUCTURAL WIREFRAME" : "ISOLINE CONTOURS"}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSubTab === "budget" && (
        <div className="space-y-6 pt-2 animate-in fade-in duration-200">
          <div className="flex justify-between items-center border-b border-border/20 pb-3">
            <h5 className="font-mono-tech text-[10px] text-primary uppercase tracking-widest">
              Estimador de Cómputos Métricos y Presupuestos (Puerto Rico)
            </h5>
            <button
              onClick={handleAutofillFromLayout}
              className="py-1 px-3 bg-void border border-primary hover:bg-primary/5 text-primary text-[9px] font-mono-tech uppercase rounded cursor-pointer transition-all font-bold"
            >
              🔄 Sincronizar desde Plano 2D
            </button>
          </div>

          {autofillSuccess && (
            <div className="bg-primary/10 border border-primary/20 text-primary text-[10px] font-mono-tech p-2.5 rounded uppercase tracking-wider">
              💡 {autofillSuccess}
            </div>
          )}

          <div className="grid lg:grid-cols-12 gap-8">
            {/* Inputs Column */}
            <div className="lg:col-span-7 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4 bg-void/45 border border-border/60 p-4 rounded-lg">
                <div className="space-y-3">
                  <span className="font-mono-tech text-[8px] text-muted-foreground uppercase tracking-widest block">Materiales Estructurales</span>
                  
                  <div className="space-y-1">
                    <label className="text-[9px] font-mono-tech text-muted-foreground uppercase flex justify-between">
                      <span>Bloques de Cemento:</span>
                      <span className="text-foreground font-bold">{blocksQty} uds</span>
                    </label>
                    <input
                      type="number"
                      value={blocksQty}
                      onChange={(e) => setBlocksQty(Math.max(0, Number(e.target.value)))}
                      className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-mono-tech text-muted-foreground uppercase flex justify-between">
                      <span>Sacos Cemento (47 lbs):</span>
                      <span className="text-foreground font-bold">{cementBags} sacos</span>
                    </label>
                    <input
                      type="number"
                      value={cementBags}
                      onChange={(e) => setCementBags(Math.max(0, Number(e.target.value)))}
                      className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Grava (m³):</label>
                      <input
                        type="number"
                        step="0.5"
                        value={gravelM3}
                        onChange={(e) => setGravelM3(Math.max(0, Number(e.target.value)))}
                        className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Arena (m³):</label>
                      <input
                        type="number"
                        step="0.5"
                        value={sandM3}
                        onChange={(e) => setSandM3(Math.max(0, Number(e.target.value)))}
                        className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-mono-tech text-muted-foreground uppercase flex justify-between">
                      <span>Varillas Acero (#4 / 20ft):</span>
                      <span className="text-foreground font-bold">{rebarQty} varillas</span>
                    </label>
                    <input
                      type="number"
                      value={rebarQty}
                      onChange={(e) => setRebarQty(Math.max(0, Number(e.target.value)))}
                      className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <span className="font-mono-tech text-[8px] text-muted-foreground uppercase tracking-widest block">Instalaciones y Recursos</span>

                  <div className="space-y-1">
                    <label className="text-[9px] font-mono-tech text-muted-foreground uppercase flex justify-between">
                      <span>Tubo PVC Eléctrico (1/2"):</span>
                      <span className="text-foreground font-bold">{conduitQty} uds</span>
                    </label>
                    <input
                      type="number"
                      value={conduitQty}
                      onChange={(e) => setConduitQty(Math.max(0, Number(e.target.value)))}
                      className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-mono-tech text-muted-foreground uppercase flex justify-between">
                      <span>Tubo PVC Hidráulico (3/4"):</span>
                      <span className="text-foreground font-bold">{pexQty} uds</span>
                    </label>
                    <input
                      type="number"
                      value={pexQty}
                      onChange={(e) => setPexQty(Math.max(0, Number(e.target.value)))}
                      className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Mano Obra ($/hr):</label>
                      <input
                        type="number"
                        value={hourlyRate}
                        onChange={(e) => setHourlyRate(Math.max(0, Number(e.target.value)))}
                        className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[8px] font-mono-tech text-muted-foreground uppercase block">Horas Lab.:</label>
                      <input
                        type="number"
                        value={hoursWorked}
                        onChange={(e) => setHoursWorked(Math.max(0, Number(e.target.value)))}
                        className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-mono-tech text-muted-foreground uppercase flex justify-between">
                      <span>Días Alquiler Mezcladora:</span>
                      <span className="text-foreground font-bold">{mixerDays} días</span>
                    </label>
                    <input
                      type="number"
                      value={mixerDays}
                      onChange={(e) => setMixerDays(Math.max(0, Number(e.target.value)))}
                      className="w-full bg-void border border-border text-xs text-foreground p-1 rounded focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Budget Summary Card */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-void border border-border/80 p-5 rounded-lg space-y-4">
                <span className="font-mono-tech text-[9px] uppercase tracking-widest text-[#00F5FF] border-b border-border/20 pb-2 block">
                  Desglose de Costes (Estimado PR)
                </span>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Bloques (a $1.85 c/u):</span>
                    <span className="text-foreground font-mono">${matBlocksCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sacos Cemento (a $9.25):</span>
                    <span className="text-foreground font-mono">${matCementCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Grava y Arena (m³):</span>
                    <span className="text-foreground font-mono">${(matGravelCost + matSandCost).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Varillas Acero (#4):</span>
                    <span className="text-foreground font-mono">${matRebarCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tuberías Conduit/PEX:</span>
                    <span className="text-foreground font-mono">${(matConduitsCost + matPexCost).toFixed(2)}</span>
                  </div>
                  
                  <div className="border-t border-border/20 pt-2 flex justify-between text-muted-foreground">
                    <span>Subtotal Materiales:</span>
                    <span className="text-foreground font-mono font-semibold">${subtotalMaterials.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal Mano de Obra:</span>
                    <span className="text-foreground font-mono font-semibold">${subtotalLabor.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal Alquiler Mezcladora:</span>
                    <span className="text-foreground font-mono font-semibold">${subtotalEquipment.toFixed(2)}</span>
                  </div>

                  <div className="border-t border-[#00F5FF]/30 pt-3 flex justify-between items-baseline text-[#00F5FF]">
                    <span className="font-bold text-sm uppercase">PRESUPUESTO TOTAL:</span>
                    <span className="font-mono text-2xl font-bold">${grandTotalBudget.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-[#00F5FF]/5 border border-[#00F5FF]/20 p-3.5 rounded text-[10px] text-[#00F5FF] leading-relaxed">
                  **Nota de Mercado:** Estimados basados en ferreterías como **National Lumber** y **Home Depot (PR)**. El costo de mano de obra refleja tarifas de ayudante y albañil local.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. Ensamble Sismorresistente 3D SubTab */}
      {activeSubTab === "block3d" && (() => {
        const steps = [
          {
            title: "Paso 1: Zapata y Cimiento (Concrete Footing)",
            desc: "Se excava y limpia una zanja de cimentación donde se instala un emparrillado de varilla de refuerzo #4. Se vierte hormigón de 3,000 PSI para formar la zapata de soporte principal.",
            points: [
              "Fijación contra fuerzas sísmicas",
              "Base de distribución de carga en el suelo",
              "Uso obligatorio de hormigón de 3,000 PSI"
            ]
          },
          {
            title: "Paso 2: Varillas de Arranque (Starter Dowels)",
            desc: "Se anclan varillas de acero #4 de manera vertical saliendo de la zapata. Para cumplir con la normativa contra huracanes de Categoría 5 (ASCE 7), las varillas deben colocarse a un espaciamiento óptimo de 16 pulgadas.",
            points: [
              "Espaciamiento de 16\" pulgadas",
              "Anclaje doblado en forma de L",
              "Varillas galvanizadas para evitar corrosión por salitre"
            ]
          },
          {
            title: "Paso 3: Asentamiento de Primera Hilada",
            desc: "Se asienta la primera línea de bloques de 8 pulgadas (Exterior Portante) usando mortero de pega de proporción 1:3. Las varillas de arranque verticales deben pasar perfectamente por las celdas huecas.",
            points: [
              "Uso de bloques de cemento de 8\" pulgadas",
              "Mortero de pega fino con junta de 3/8\"",
              "Alineación precisa con plomada y cordel"
            ]
          },
          {
            title: "Paso 4: Relleno de Concreto (Grouting cells)",
            desc: "Se vierte hormigón fino de alta fluidez (grout) dentro de las celdas que alojan la varilla de acero vertical para rigidizar la estructura de forma monolítica.",
            points: [
              "Hormigón de relleno fino y fluido",
              "Pilares de concreto continuos y macizos",
              "Compactación por vibración ligera"
            ]
          },
          {
            title: "Paso 5: Viga Corona perimetral (Bond Beam)",
            desc: "En la hilada superior del muro, se colocan bloques en U con varillas horizontales y se rellenan de concreto, amarrando estructuralmente todo el perímetro del techo.",
            points: [
              "Amarre de muros perimetrales superiores",
              "Evita el vuelco o levantamiento del techo por viento",
              "Integración de ganchos roscados para planchas de anclaje"
            ]
          }
        ];

        const currentStep = steps[assemblyStep] || steps[0];

        return (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-in fade-in duration-200 text-left">
            {/* Left side: Guide and step details */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-void/50 border border-border/80 p-5 rounded-lg space-y-4">
                <div className="flex items-center justify-between border-b border-border/20 pb-2">
                  <span className="font-mono-tech text-[8px] text-[#39FF14] uppercase tracking-widest font-bold">
                    Paso {assemblyStep + 1} de {steps.length}
                  </span>
                  <span className="text-[7.5px] bg-[#39FF14]/25 text-[#39FF14] border border-[#39FF14]/40 px-1.5 py-0.5 rounded font-bold uppercase font-mono-tech">
                    Guía de Obra PR
                  </span>
                </div>

                <div className="space-y-1">
                  <h5 className="font-serif-display text-base text-foreground font-bold leading-tight">
                    {currentStep.title}
                  </h5>
                  <p className="text-[10.5px] text-muted-foreground leading-relaxed">
                    {currentStep.desc}
                  </p>
                </div>

                <div className="space-y-2 border-t border-border/20 pt-3">
                  <span className="font-mono-tech text-[8px] text-primary uppercase tracking-widest font-bold block">
                    Pautas de Seguridad y Resiliencia
                  </span>
                  <ul className="space-y-1.5 text-[9px] text-muted-foreground list-disc list-inside">
                    {currentStep.points.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>

                <div className="flex gap-2 border-t border-border/20 pt-4 select-none">
                  <button
                    disabled={assemblyStep === 0}
                    onClick={() => setAssemblyStep(prev => Math.max(0, prev - 1))}
                    className="flex-1 py-1.5 bg-void border border-border hover:border-primary text-foreground text-[10px] font-mono-tech uppercase rounded cursor-pointer transition-all disabled:opacity-30"
                  >
                    ⏮️ Anterior
                  </button>
                  <button
                    disabled={assemblyStep === steps.length - 1}
                    onClick={() => setAssemblyStep(prev => Math.min(steps.length - 1, prev + 1))}
                    className="flex-1 py-1.5 bg-[#39FF14] text-black hover:bg-[#39FF14]/90 text-[10px] font-mono-tech font-bold uppercase rounded cursor-pointer transition-all disabled:opacity-30"
                  >
                    Siguiente ⏭️
                  </button>
                </div>
              </div>

              <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 p-4 rounded-lg text-[9px] text-muted-foreground leading-relaxed">
                💡 <strong>Consejo del Taller:</strong> De conformidad con la normativa sismorresistente local, las paredes exteriores de bloques de hormigón en Puerto Rico deben anclarse y rigidizarse con pilares de concreto cada 8 a 10 pies de distancia lineal para resistir sismos prolongados y huracanes categoría 5.
              </div>
            </div>

            {/* Right side: SVG Visual details */}
            <div className="lg:col-span-7 bg-[#05070d] border border-border/70 rounded-lg p-6 flex flex-col justify-center items-center relative overflow-hidden min-h-[300px]">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(57,255,20,0.03),transparent_50%)] pointer-events-none" />

              <svg viewBox="0 0 200 150" className="w-full max-w-[420px] h-[300px]">
                {/* Ground floor line */}
                <line x1="10" y1="120" x2="190" y2="120" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                
                {/* 1. Footing zapata render */}
                <rect x="40" y="110" width="120" height="25" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" />
                <path d="M 45,122 L 155,122 M 45,130 L 155,130" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeDasharray="2,1" />

                {assemblyStep >= 0 && (
                  <rect x="40" y="110" width="120" height="25" fill="rgba(255, 255, 255, 0.08)" stroke="#a855f7" strokeWidth="1.5" />
                )}

                {/* 2. Starter Rebars render */}
                {assemblyStep >= 1 && (
                  <>
                    {/* Rebars vertical lines */}
                    <path d="M 60,132 L 60,60 M 100,132 L 100,60 M 140,132 L 140,60" fill="none" stroke="#FF007F" strokeWidth="1.8" className="animate-pulse" />
                    {/* Bent L anchor lines */}
                    <path d="M 60,132 L 72,132 M 100,132 L 112,132 M 140,132 L 152,132" fill="none" stroke="#FF007F" strokeWidth="1.8" />
                    {/* Spacing label text */}
                    <text x="80" y="55" fill="#FF007F" fontSize="5" textAnchor="middle" fontFamily="monospace">varilla #4 @ 16" spacing</text>
                  </>
                )}

                {/* 3. First block course */}
                {assemblyStep >= 2 && (
                  <>
                    {/* Blocks course grid overlay */}
                    <g fill="rgba(56, 189, 248, 0.12)" stroke="#38bdf8" strokeWidth="1">
                      <rect x="48" y="94" width="32" height="16" />
                      <rect x="84" y="94" width="32" height="16" />
                      <rect x="120" y="94" width="32" height="16" />
                      <rect x="48" y="78" width="32" height="16" />
                      <rect x="84" y="78" width="32" height="16" />
                      <rect x="120" y="78" width="32" height="16" />
                    </g>
                    {/* Hollow cells details */}
                    <line x1="64" y1="94" x2="64" y2="110" stroke="rgba(56, 189, 248, 0.4)" strokeWidth="1" strokeDasharray="1,1" />
                    <line x1="100" y1="94" x2="100" y2="110" stroke="rgba(56, 189, 248, 0.4)" strokeWidth="1" strokeDasharray="1,1" />
                  </>
                )}

                {/* 4. Concrete Pouring/Grouting */}
                {assemblyStep >= 3 && (
                  <>
                    <rect x="52" y="78" width="16" height="32" fill="rgba(57, 255, 20, 0.22)" stroke="#39FF14" strokeWidth="0.8" />
                    <rect x="92" y="78" width="16" height="32" fill="rgba(57, 255, 20, 0.22)" stroke="#39FF14" strokeWidth="0.8" />
                    <rect x="132" y="78" width="16" height="32" fill="rgba(57, 255, 20, 0.22)" stroke="#39FF14" strokeWidth="0.8" />
                    <text x="100" y="73" fill="#39FF14" fontSize="4.5" textAnchor="middle" fontFamily="monospace" className="font-bold">Hormigón Vertido (Grout 3,000 PSI)</text>
                  </>
                )}

                {/* 5. Bond Beam corona */}
                {assemblyStep >= 4 && (
                  <>
                    <rect x="44" y="60" width="112" height="18" fill="rgba(255, 107, 53, 0.28)" stroke="#FF6B35" strokeWidth="1.5" />
                    {/* Horizontal rebar inside bond beam */}
                    <line x1="48" y1="69" x2="152" y2="69" stroke="#FF007F" strokeWidth="1.8" />
                    <text x="100" y="50" fill="#FF6B35" fontSize="5" textAnchor="middle" fontFamily="monospace" className="font-bold">Viga Corona Superior (Amarre)</text>
                  </>
                )}
              </svg>

              <span className="absolute bottom-4 right-4 text-[7px] text-muted-foreground/60 font-mono-tech uppercase">
                {currentStep.title} // Prometheus CAD Engine
              </span>
            </div>
          </div>
        );
      })()}

      {activeSubTab === "aerodynamics" && (() => {
        const area = roofWidth * roofLength;
        const vMps = windSpeedMph * 0.44704; // mph to m/s
        const rho = 1.2; // kg/m^3 air density

        // Cp coefficients based on roof aerodynamics
        const cp = {
          flat: { val: 1.2, name: "Techo Plano (Elevada Succión)" },
          gable: { val: 0.8, name: "Techo a Dos Aguas (Succión Media)" },
          hip: { val: 0.4, name: "Techo a Cuatro Aguas (Diseño Aerodinámico)" }
        }[roofType];

        // Uplift Pressure P = 0.5 * rho * v^2 * Cp
        const pUpliftPa = 0.5 * rho * Math.pow(vMps, 2) * cp.val;
        const pUpliftKpa = pUpliftPa / 1000;
        const fLiftKn = (pUpliftPa * area) / 1000;
        
        // Dead load of a 15cm reinforced concrete slab in PR (~3.6 kN/m^2)
        const deadLoadKpa = 3.6;
        const netUpliftKpa = Math.max(0, pUpliftKpa - deadLoadKpa);
        const warningTriggered = pUpliftKpa > deadLoadKpa && windSpeedMph > 74;

        return (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-in fade-in duration-200 text-left">
            {/* Left Controls */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-[#0b0f19] border border-border/80 p-5 rounded-lg space-y-4 font-mono-tech text-[10px]">
                <div className="flex justify-between items-center border-b border-border/20 pb-2.5">
                  <span className="text-[9px] text-primary uppercase tracking-widest font-bold">Túnel de Viento & Aerodinámica</span>
                  <span className="text-[7.5px] bg-primary/20 text-primary border border-primary/30 px-1.5 py-0.5 rounded font-bold">ASCE 7-16</span>
                </div>

                {/* Roof Type Selection */}
                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold text-muted-foreground uppercase">Geometría del Techo:</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(["flat", "gable", "hip"] as const).map((type) => (
                      <button
                        key={type}
                        onClick={() => setRoofType(type)}
                        className={`py-2 rounded font-mono-tech text-[9px] font-bold uppercase transition-all cursor-pointer border ${
                          roofType === type
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-void border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {type === "flat" ? "🏢 Plano" : type === "gable" ? "🏠 Dos Aguas" : "🛡️ Hip (4 Aguas)"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Wind Speed Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-muted-foreground">
                    <span>💨 Velocidad del Viento:</span>
                    <span className="text-primary font-bold">{windSpeedMph} MPH</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    step="5"
                    value={windSpeedMph}
                    onChange={(e) => setWindSpeedMph(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-[7px] text-muted-foreground/60 uppercase">
                    <span>Brisa (0 mph)</span>
                    <span>Categoría 5+ (200 mph)</span>
                  </div>
                </div>

                {/* Dimensions */}
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1">
                    <span className="text-muted-foreground">Ancho del Techo (m):</span>
                    <input
                      type="number"
                      value={roofWidth}
                      onChange={(e) => setRoofWidth(Math.max(1, Number(e.target.value)))}
                      className="w-full bg-void border border-border rounded p-1 text-foreground text-[9px] focus:outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-muted-foreground">Largo del Techo (m):</span>
                    <input
                      type="number"
                      value={roofLength}
                      onChange={(e) => setRoofLength(Math.max(1, Number(e.target.value)))}
                      className="w-full bg-void border border-border rounded p-1 text-foreground text-[9px] focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Physical results details */}
              <div className="bg-void/60 border border-border/80 p-4 rounded-lg space-y-2.5 font-mono-tech text-[9.5px] text-muted-foreground">
                <span className="text-[8px] text-primary uppercase font-bold tracking-widest block">Análisis de Cargas de Succión</span>
                <div className="space-y-1.5">
                  <div className="flex justify-between">
                    <span>Área del Techo:</span>
                    <strong className="text-foreground">{area} m²</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Coeficiente de Presión (Cp):</span>
                    <strong className="text-foreground">-{cp.val} ({roofType.toUpperCase()})</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Presión de Succión Neta:</span>
                    <strong className="text-foreground">{pUpliftKpa.toFixed(2)} kN/m²</strong>
                  </div>
                  <div className="flex justify-between border-t border-border/20 pt-1">
                    <span>Fuerza de Levantamiento Total:</span>
                    <strong className="text-primary text-xs">{(fLiftKn).toFixed(1)} kN</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Carga Muerta del Techo (15cm Concreto):</span>
                    <strong className="text-foreground">-{deadLoadKpa.toFixed(1)} kN/m²</strong>
                  </div>
                  <div className="flex justify-between border-t border-border/20 pt-1 font-bold text-foreground">
                    <span>Resistencia del Anclaje:</span>
                    <span className={warningTriggered ? "text-red-400" : "text-emerald-400"}>
                      {warningTriggered ? "⚠️ FALLA CRÍTICA / REPLIEGUE" : "✅ ANCLAJE SEGURO"}
                    </span>
                  </div>
                </div>
              </div>

              {warningTriggered && (
                <div className="bg-red-950/20 border border-red-500/40 p-3 rounded text-[8.5px] text-red-400 leading-normal animate-pulse font-mono-tech">
                  ⚠️ <strong>Alerta de Succión Extrema:</strong> La fuerza de levantamiento hidrodinámico supera la gravedad del techo de concreto. Es indispensable varillas verticales #4 ancladas al cimiento a 16" y vigas corona sismorresistentes para evitar el levantamiento por vientos de huracán.
                </div>
              )}
            </div>

            {/* Right Column: Wind Tunnel Vector field */}
            <div className="lg:col-span-7 bg-[#05070d] border border-border/80 rounded-lg p-6 flex flex-col items-center justify-center relative min-h-[300px] overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(56,189,248,0.03),transparent_70%)] pointer-events-none" />

              <h6 className="font-mono-tech text-[8px] text-muted-foreground/60 uppercase absolute top-4 left-4 tracking-widest">
                Visualizador del Túnel de Viento (Vectores de Presión)
              </h6>

              <svg viewBox="0 0 240 180" className="w-full max-w-[420px] h-[260px]">
                {/* 1. Draw House outline based on roof type */}
                {/* House Base */}
                <rect x="80" y="110" width="80" height="50" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                <line x1="80" y1="110" x2="160" y2="110" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

                {/* Roof Outlines */}
                {roofType === "flat" && (
                  <>
                    <line x1="75" y1="110" x2="165" y2="110" stroke="#00f5ff" strokeWidth="2.5" />
                    {/* Turbulence / Suction swirl at back (low pressure) */}
                    <path d="M 165,110 C 185,90 190,130 170,140 C 160,145 155,130 165,120" fill="none" stroke="#f87171" strokeWidth="1" strokeDasharray="2,2" className="animate-pulse" />
                    <text x="175" y="95" fill="#f87171" fontSize="4" fontFamily="monospace">Succión Crítica</text>
                  </>
                )}

                {roofType === "gable" && (
                  <>
                    <polygon points="75,110 120,80 165,110" fill="none" stroke="#00f5ff" strokeWidth="2.5" />
                    {/* Air detachment at peak */}
                    <path d="M 120,80 C 140,65 150,90 135,100" fill="none" stroke="#fbbf24" strokeWidth="1" strokeDasharray="2,2" />
                  </>
                )}

                {roofType === "hip" && (
                  <>
                    <polygon points="75,110 95,90 145,90 165,110" fill="none" stroke="#00f5ff" strokeWidth="2.5" />
                    {/* Aerodynamic wind flows */}
                    <path d="M 95,90 L 145,90" stroke="#39FF14" strokeWidth="1" opacity="0.3" />
                  </>
                )}

                {/* Wind Vector Lines (animated particles/arrows moving left-to-right) */}
                {/* Wind direction is left to right */}
                <path d="M 10,70 Q 75,70 85,95 Q 95,110 120,110 Q 150,110 230,120" fill="none" stroke={roofType === "hip" ? "#39FF14" : "#f87171"} strokeWidth="1" opacity="0.6" strokeDasharray="3,3" />
                <path d="M 10,50 Q 75,50 95,75 Q 115,90 150,90 Q 185,90 230,100" fill="none" stroke={roofType === "hip" ? "#39FF14" : "#fbbf24"} strokeWidth="1" opacity="0.6" strokeDasharray="4,4" />
                <path d="M 10,30 Q 80,30 110,55 Q 130,70 170,75 Q 200,80 230,85" fill="none" stroke="#39FF14" strokeWidth="1" opacity="0.7" strokeDasharray="3,3" />

                {/* Suction arrows on roof pointing UP */}
                {windSpeedMph > 40 && (
                  <>
                    <line x1="100" y1="100" x2="100" y2="85" stroke="#ef4444" strokeWidth="1.2" markerEnd="url(#arrow)" />
                    <line x1="120" y1="100" x2="120" y2="80" stroke="#ef4444" strokeWidth="1.2" markerEnd="url(#arrow)" />
                    <line x1="140" y1="100" x2="140" y2="85" stroke="#ef4444" strokeWidth="1.2" markerEnd="url(#arrow)" />
                    <text x="120" y="73" fill="#ef4444" fontSize="4.5" textAnchor="middle" fontFamily="monospace" className="font-bold">SUCCIÓN DE VIENTO (LIFT)</text>
                  </>
                )}

                {/* Definition of arrow marker */}
                <defs>
                  <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="3" markerHeight="3" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#ef4444" />
                  </marker>
                </defs>
              </svg>

              <span className="absolute bottom-4 right-4 text-[7px] text-muted-foreground/60 font-mono-tech uppercase">
                Wind Suction Telemetry // ASCE 7-16
              </span>
            </div>
          </div>
        );
      })()}

      {/* 12. Rainwater Harvesting & Gravity Filtration (150% Pro Feature) */}
      {activeSubTab === "rain-harvest" && (() => {
        // Runoff calculation
        // roofArea = roofWidth * roofLength
        const roofArea = roofWidth * roofLength;
        // Annual Harvested Gallons = Area (m2) * rainfall (mm) * 0.264172 * runoff (0.85)
        const annualHarvestedGal = roofArea * annualRainfall * 0.264172 * 0.85;

        // Filtration parameters
        const flowRateGpm = 12 * (1 - (leafClogPct / 100));
        
        // Output Turbidity calculation (WHO standard is < 5 NTU for drinking water)
        const outputTurbidity = Number((
          0.8 + 
          (100 - filterCarbonStatus) * 0.08 + 
          (100 - filterGravelStatus) * 0.15 + 
          (leafClogPct > 80 ? 12.5 : 0)
        ).toFixed(2));

        const isSafeToDrink = outputTurbidity < 5.0 && uvLampWatts >= 15;

        const cleanFilters = () => {
          setFilterGravelStatus(100);
          setFilterCarbonStatus(100);
          toast.success("Filtros lavados y reactivados.");
        };

        const clearLeaves = () => {
          setLeafClogPct(0);
          toast.success("Malla limpia y libre de hojas.");
        };

        return (
          <div className="grid lg:grid-cols-12 gap-6 animate-in fade-in duration-200 text-left">
            {/* Left Controls */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-[#0b0f19] border border-border/80 p-5 rounded-lg space-y-4 font-mono-tech text-[10px]">
                <div className="flex justify-between items-center border-b border-border/20 pb-2.5">
                  <span className="text-[9px] text-[#0284c7] uppercase tracking-widest font-bold">Consola de Cosecha de Lluvia</span>
                  <span className="text-[7.5px] bg-[#0284c7]/20 text-[#0284c7] border border-[#0284c7]/30 px-1.5 py-0.5 rounded font-bold uppercase">Filtro Pluvial</span>
                </div>

                {/* Leaf Clog Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-muted-foreground">
                    <span>🍂 Obstrucción de Hojas en Malla:</span>
                    <span className="text-sky-400 font-bold">{leafClogPct}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={leafClogPct}
                    onChange={(e) => setLeafClogPct(Number(e.target.value))}
                    className="w-full accent-sky-400 bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                  />
                </div>

                {/* Gravel Cleanliness Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-muted-foreground">
                    <span>🪨 Eficiencia Filtro de Grava:</span>
                    <span className="text-sky-400 font-bold">{filterGravelStatus}%</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="5"
                    value={filterGravelStatus}
                    onChange={(e) => setFilterGravelStatus(Number(e.target.value))}
                    className="w-full accent-sky-400 bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                  />
                </div>

                {/* Carbon Cleanliness Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-muted-foreground">
                    <span>🧪 Carbón Activado Remanente:</span>
                    <span className="text-sky-400 font-bold">{filterCarbonStatus}%</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="5"
                    value={filterCarbonStatus}
                    onChange={(e) => setFilterCarbonStatus(Number(e.target.value))}
                    className="w-full accent-sky-400 bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                  />
                </div>

                {/* UV Lamp slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-muted-foreground">
                    <span>🔮 Potencia Lámpara UV:</span>
                    <span className="text-sky-400 font-bold">{uvLampWatts} Watts</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="40"
                    step="5"
                    value={uvLampWatts}
                    onChange={(e) => setUvLampWatts(Number(e.target.value))}
                    className="w-full accent-sky-400 bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                  />
                </div>

                {/* Clean actions */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/20">
                  <button
                    onClick={cleanFilters}
                    className="py-1.5 bg-void border border-sky-500/40 hover:border-sky-400 text-sky-400 rounded cursor-pointer transition-all uppercase text-[8px] font-bold"
                  >
                    🪨 Lavar Filtros
                  </button>
                  <button
                    onClick={clearLeaves}
                    className="py-1.5 bg-void border border-green-500/40 hover:border-green-400 text-green-400 rounded cursor-pointer transition-all uppercase text-[8px] font-bold"
                  >
                    🍂 Limpiar Hojas
                  </button>
                </div>
              </div>

              {/* Data Table */}
              <div className="bg-void/60 border border-border/80 p-4 rounded-lg space-y-2.5 font-mono-tech text-[9.5px] text-muted-foreground">
                <span className="text-[8px] text-sky-400 uppercase font-bold tracking-widest block">Telemetría de Cosecha & Calidad</span>
                <div className="space-y-1.5">
                  <div className="flex justify-between">
                    <span>Área del Techo Captador:</span>
                    <strong className="text-foreground">{roofArea} m²</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Volumen Pluvial Cosechado:</span>
                    <strong className="text-[#0284c7]">{annualHarvestedGal.toLocaleString(undefined, {maximumFractionDigits: 0})} Gal/Año</strong>
                  </div>
                  <div className="flex justify-between border-b border-border/20 pb-1">
                    <span>Flujo de Entrada Actual:</span>
                    <strong className="text-foreground">{flowRateGpm.toFixed(1)} GPM</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Turbidez de Salida:</span>
                    <strong className={outputTurbidity > 5.0 ? "text-red-400" : "text-emerald-400"}>
                      {outputTurbidity} NTU
                    </strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Esterilización Bacterial (UV):</span>
                    <strong className={uvLampWatts >= 15 ? "text-emerald-400" : "text-red-400"}>
                      {uvLampWatts >= 15 ? "99.99% DESINFECTADO" : "INSUFICIENTE"}
                    </strong>
                  </div>
                  <div className="flex justify-between border-t border-border/20 pt-1 font-bold text-foreground">
                    <span>Aptitud para Consumo Humano:</span>
                    <span className={isSafeToDrink ? "text-[#39FF14]" : "text-red-400"}>
                      {isSafeToDrink ? "✅ EXCELENTE (Potable)" : "❌ REQUIERE TRATAMIENTO"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Filtration Tower SVG */}
            <div className="lg:col-span-7 bg-[#05070d] border border-border/80 rounded-lg p-6 flex flex-col justify-between relative min-h-[340px] overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(2,132,199,0.03),transparent_70%)] pointer-events-none" />

              <h6 className="font-mono-tech text-[8px] text-muted-foreground/60 uppercase absolute top-4 left-4 tracking-widest">
                Esquema de la Torre de Filtración y Desinfección Pluvial
              </h6>

              <div className="flex justify-center items-center h-48 pt-4">
                <svg viewBox="0 0 100 110" className="w-40 h-36">
                  {/* Top rainwater inlet funnel */}
                  <path d="M 20,5 L 80,5 L 70,20 L 30,20 Z" fill="none" stroke="#fff" strokeWidth="1" />
                  <text x="50" y="14" fill="#0284c7" fontSize="5" textAnchor="middle" fontFamily="monospace">ENTRADA PLUVIAL</text>

                  {/* Water drops falling from inlet */}
                  {flowRateGpm > 0 && (
                    <>
                      <circle cx="45" cy="23" r="1" fill="#38bdf8" />
                      <circle cx="55" cy="27" r="1" fill="#38bdf8" />
                    </>
                  )}

                  {/* Tower shell column */}
                  <rect x="35" y="20" width="30" height="70" fill="none" stroke="#64748b" strokeWidth="1" />

                  {/* Step 1: Leaf screen */}
                  <line x1="35" y1="35" x2="65" y2="35" stroke="#ef4444" strokeWidth="1.5" />
                  {leafClogPct > 30 && (
                    <circle cx="50" cy="33" r="2.5" fill="#78350f" opacity="0.8" />
                  )}

                  {/* Step 2: Gravel bed */}
                  <rect x="36" y="42" width="28" height="12" fill="rgba(148,163,184,0.3)" />
                  <text x="50" y="50" fill="#fff" fontSize="3.5" textAnchor="middle" fontFamily="monospace">GRAVA</text>

                  {/* Step 3: Carbon bed */}
                  <rect x="36" y="58" width="28" height="12" fill="rgba(30,41,59,0.7)" />
                  <text x="50" y="66" fill="#fff" fontSize="3.5" textAnchor="middle" fontFamily="monospace">CARBÓN ACT.</text>

                  {/* Step 4: UV radiation chamber */}
                  <rect x="36" y="74" width="28" height="14" fill={uvLampWatts >= 15 ? "rgba(168,85,247,0.35)" : "none"} />
                  {uvLampWatts >= 15 && (
                    <line x1="50" y1="74" x2="50" y2="88" stroke="#a855f7" strokeWidth="2.5" className="animate-pulse" />
                  )}
                  <text x="50" y="82" fill={uvLampWatts >= 15 ? "#a855f7" : "#64748b"} fontSize="3.5" textAnchor="middle" fontFamily="monospace">CÁMARA UV</text>

                  {/* Distilled storage base cistern tank connection */}
                  <path d="M 35,90 L 15,90 L 15,108 L 85,108 L 85,90 L 65,90" fill="none" stroke="#0284c7" strokeWidth="1.2" />
                  <rect x="17" y="93" width="66" height="13" fill={isSafeToDrink ? "rgba(57,255,20,0.15)" : "rgba(239,68,68,0.1)"} />
                  <text x="50" y="101" fill={isSafeToDrink ? "#39FF14" : "#ef4444"} fontSize="4.5" textAnchor="middle" fontFamily="monospace" className="font-bold">
                    {isSafeToDrink ? "CISTERNA POTABLE" : "AGUA NO POTABLE"}
                  </text>
                </svg>
              </div>

              <div className="border-t border-border/20 pt-3 mt-4 text-[7px] text-muted-foreground/60 font-mono-tech uppercase flex justify-between font-bold">
                <span>GRAVITY COLUMN SYSTEM // PURITY ANALYZER</span>
                <span>WHO DRINKING WATER STANDARD</span>
              </div>
            </div>
          </div>
        );
      })()}

      {activeSubTab === "thermal" && (() => {
        // Sol-Air Temperature (includes solar absorption based on roof surface color/albedo)
        // A lower albedo absorbs more solar radiation, increasing roof surface temperature.
        const solAirTemp = thermalOutTemp + (1 - thermalAlbedo) * 15;

        // Conductive thermal resistances (R-value in m²·K/W)
        // R = thickness (m) / thermal conductivity (W/m·K)
        const rExteriorFilm = 0.04;
        const rInteriorFilm = 0.13;
        
        const concreteThicknessM = thermalConcreteInches * 0.0254;
        const rConcrete = concreteThicknessM > 0 ? concreteThicknessM / 1.3 : 0; // k_concrete = 1.3 W/m·K

        const insulationThicknessM = thermalInsulationInches * 0.0254;
        const rInsulation = insulationThicknessM > 0 ? insulationThicknessM / 0.03 : 0; // k_EPS = 0.03 W/m·K

        const rDrywall = thermalDrywallEnabled ? 0.0127 / 0.17 : 0; // 1/2" drywall, k_drywall = 0.17 W/m·K

        const rTotal = rExteriorFilm + rConcrete + rInsulation + rDrywall + rInteriorFilm;
        const uValue = 1 / rTotal; // W/m²·K

        // Heat gain flow Q (Watts for a typical 100 m² ceiling area)
        const areaM2 = 100;
        const heatFlowQ = uValue * areaM2 * Math.max(0, solAirTemp - thermalInTemp);

        // Baseline (Dark concrete roof, no insulation)
        const baselineR = rExteriorFilm + (6 * 0.0254 / 1.3) + rInteriorFilm;
        const baselineSolAir = thermalOutTemp + (1 - 0.15) * 15;
        const baselineQ = (1 / baselineR) * areaM2 * Math.max(0, baselineSolAir - thermalInTemp);
        
        const savingsWatts = Math.max(0, baselineQ - heatFlowQ);
        const monthlySavingsDollars = savingsWatts * 0.001 * 8 * 30 * 0.28; // 8 hrs daily A/C runtime, $0.28/kWh rate in PR

        // Interfaces temperatures mapping
        const tSurfOut = solAirTemp;
        const tConcreteIn = tSurfOut - (rExteriorFilm / rTotal) * (solAirTemp - thermalInTemp);
        const tInsulationIn = tConcreteIn - (rConcrete / rTotal) * (solAirTemp - thermalInTemp);
        const tDrywallIn = tInsulationIn - (rInsulation / rTotal) * (solAirTemp - thermalInTemp);
        const tSurfIn = tDrywallIn - (rDrywall / rTotal) * (solAirTemp - thermalInTemp);

        return (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-in fade-in duration-200 text-left">
            {/* Left Column - Controls */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-void/50 border border-border/80 p-5 rounded-lg space-y-4 font-mono-tech text-[10px]">
                <div className="flex justify-between items-center border-b border-border/20 pb-2.5">
                  <span className="text-[9px] text-[#39FF14] uppercase tracking-widest font-bold">Gradiente Térmico de Techos</span>
                  <span className="text-[7.5px] bg-[#39FF14]/25 text-[#39FF14] border border-[#39FF14]/40 px-1.5 py-0.5 rounded font-bold uppercase">Thermal Lab</span>
                </div>

                {/* Exterior Air Temp slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-[9px] font-bold">
                    <span className="text-muted-foreground uppercase">Temperatura Aire Exterior (PR):</span>
                    <span className="text-primary-glow">{thermalOutTemp} °C</span>
                  </div>
                  <input
                    type="range"
                    min="25"
                    max="48"
                    step="1"
                    value={thermalOutTemp}
                    onChange={(e) => setThermalOutTemp(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                  />
                </div>

                {/* Thermostat Temp slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-[9px] font-bold">
                    <span className="text-muted-foreground uppercase">Termostato Interior A/C:</span>
                    <span className="text-primary-glow">{thermalInTemp} °C</span>
                  </div>
                  <input
                    type="range"
                    min="18"
                    max="28"
                    step="1"
                    value={thermalInTemp}
                    onChange={(e) => setThermalInTemp(Number(e.target.value))}
                    className="w-full accent-primary bg-void border border-border rounded h-1 appearance-none cursor-pointer"
                  />
                </div>

                {/* Roof Paint/Albedo toggle */}
                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold text-muted-foreground uppercase">Tratamiento de Pintura del Techo:</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setThermalAlbedo(0.15)}
                      className={`py-1.5 rounded border text-[8.5px] font-bold cursor-pointer transition-all ${
                        thermalAlbedo === 0.15 
                          ? "bg-red-500/20 border-red-500 text-red-400" 
                          : "bg-void/60 border-border/40 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Sin Pintura (Losa Oscura)
                    </button>
                    <button
                      onClick={() => setThermalAlbedo(0.85)}
                      className={`py-1.5 rounded border text-[8.5px] font-bold cursor-pointer transition-all ${
                        thermalAlbedo === 0.85 
                          ? "bg-primary/20 border-primary text-primary-glow" 
                          : "bg-void/60 border-border/40 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Blanco Reflectivo (Cool Roof)
                    </button>
                  </div>
                </div>

                {/* Concrete thickness selector */}
                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold text-muted-foreground uppercase">Espesor de Losa de Hormigón:</label>
                  <div className="grid grid-cols-3 gap-2">
                    {([4, 6, 8] as const).map((inches) => (
                      <button
                        key={inches}
                        onClick={() => setThermalConcreteInches(inches)}
                        className={`py-1 rounded border text-[9px] font-bold cursor-pointer transition-all ${
                          thermalConcreteInches === inches 
                            ? "bg-primary/20 border-primary text-primary-glow" 
                            : "bg-void/60 border-border/40 text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {inches}" (~{Math.round(inches * 2.54)} cm)
                      </button>
                    ))}
                  </div>
                </div>

                {/* Insulation EPS selector */}
                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold text-muted-foreground uppercase">Espesor Placa Aislamiento (EPS):</label>
                  <div className="grid grid-cols-5 gap-1.5">
                    {([0, 1, 2, 3, 4] as const).map((inches) => (
                      <button
                        key={inches}
                        onClick={() => setThermalInsulationInches(inches)}
                        className={`py-1 rounded border text-[9px] font-bold cursor-pointer transition-all ${
                          thermalInsulationInches === inches 
                            ? "bg-primary/20 border-primary text-primary-glow" 
                            : "bg-void/60 border-border/40 text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {inches}"
                      </button>
                    ))}
                  </div>
                </div>

                {/* Drywall toggle */}
                <div className="flex items-center justify-between border-t border-border/20 pt-2 text-[9px] font-bold text-muted-foreground">
                  <span className="uppercase">Instalar Plafón Drywall (Cielo Raso):</span>
                  <input
                    type="checkbox"
                    checked={thermalDrywallEnabled}
                    onChange={(e) => setThermalDrywallEnabled(e.target.checked)}
                    className="accent-primary w-3.5 h-3.5 cursor-pointer"
                  />
                </div>
              </div>

              {/* Technical context for PR */}
              <div className="bg-void/40 border border-border p-4 rounded-lg space-y-2 font-mono text-[9px] text-muted-foreground leading-normal">
                <p>💡 **Física Térmica en el Trópico**: Los techos oscuros en Puerto Rico absorben hasta el 85% de la radiación solar directa, elevando la superficie del hormigón más de 15°C por encima del aire. Aplicar un sellador de techos blanco reflectivo reduce drásticamente el flujo de calor inicial ($Q$).</p>
              </div>
            </div>

            {/* Right Column - Results & Interactive SVG */}
            <div className="lg:col-span-7 space-y-4">
              <div className="bg-void/50 border border-border/80 p-5 rounded-lg grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div className="bg-void border border-border/40 p-3 rounded">
                  <span className="text-[7.5px] text-muted-foreground uppercase block font-bold">Valor U (Transmitancia)</span>
                  <span className="text-xs font-bold text-red-400 font-mono-tech">{uValue.toFixed(3)} W/m²·K</span>
                </div>
                <div className="bg-void border border-border/40 p-3 rounded">
                  <span className="text-[7.5px] text-muted-foreground uppercase block font-bold">Temperatura Techo Ext.</span>
                  <span className="text-xs font-bold text-amber-400 font-mono-tech">{solAirTemp.toFixed(1)} °C</span>
                </div>
                <div className="bg-void border border-border/40 p-3 rounded">
                  <span className="text-[7.5px] text-muted-foreground uppercase block font-bold">Carga de Calor Techo</span>
                  <span className="text-xs font-bold text-orange-400 font-mono-tech">{heatFlowQ.toFixed(0)} W</span>
                </div>
                <div className="bg-void border border-border/40 p-3 rounded">
                  <span className="text-[7.5px] text-muted-foreground uppercase block font-bold">Ahorro Mensual A/C</span>
                  <span className="text-xs font-bold text-[#39FF14] font-mono-tech">${monthlySavingsDollars.toFixed(2)}</span>
                </div>
              </div>

              {/* SVG Graphic Gradient representation */}
              <div className="bg-slate-950 border border-slate-900 rounded-lg p-4 relative overflow-hidden flex flex-col items-center">
                <span className="font-mono-tech text-[8px] text-slate-500 uppercase tracking-widest self-start mb-2">Análisis de Transferencia y Gradiente Térmico</span>

                <svg className="w-full h-56 max-w-[480px]" viewBox="0 0 300 150">
                  <defs>
                    <linearGradient id="heatGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#ef4444" /> {/* Hot red exterior */}
                      <stop offset="100%" stopColor="#3b82f6" /> {/* Cool blue interior */}
                    </linearGradient>
                  </defs>

                  {/* Grid layout */}
                  <rect width="300" height="150" fill="none" />

                  {/* Exterior Air (Hot Sun) */}
                  <rect x="0" y="20" width="40" height="110" fill="rgba(239,68,68,0.15)" />
                  <text x="20" y="75" fill="#ef4444" fontSize="4.5" textAnchor="middle" fontFamily="monospace" transform="rotate(-90, 20, 75)">AIRE EXTERIOR</text>

                  {/* Layer 1: Concrete block */}
                  <rect x="40" y="20" width="80" height="110" fill="rgba(148,163,184,0.3)" stroke="#64748b" strokeWidth="0.8" />
                  <text x="80" y="32" fill="#94a3b8" fontSize="4.5" textAnchor="middle" fontFamily="monospace">LOSA HORMIGÓN ({thermalConcreteInches}")</text>

                  {/* Layer 2: Insulation block (drawn if thickness > 0) */}
                  {thermalInsulationInches > 0 ? (
                    <>
                      <rect x="120" y="20" width="60" height="110" fill="rgba(234,179,8,0.2)" stroke="#eab308" strokeWidth="0.8" strokeDasharray="3,3" />
                      <text x="150" y="32" fill="#eab308" fontSize="4.5" textAnchor="middle" fontFamily="monospace">AISLANTE ({thermalInsulationInches}")</text>
                    </>
                  ) : (
                    <line x1="120" y1="20" x2="120" y2="130" stroke="#475569" strokeWidth="1" />
                  )}

                  {/* Layer 3: Drywall block (drawn if enabled) */}
                  {thermalDrywallEnabled ? (
                    <>
                      <rect x="180" y="20" width="30" height="110" fill="rgba(254,243,199,0.35)" stroke="#d97706" strokeWidth="0.8" />
                      <text x="195" y="32" fill="#d97706" fontSize="4.5" textAnchor="middle" fontFamily="monospace" transform="rotate(90, 195, 32)">DRYWALL</text>
                    </>
                  ) : (
                    <line x1="180" y1="20" x2="180" y2="130" stroke="#475569" strokeWidth="1" />
                  )}

                  {/* Interior Air (Thermostat target) */}
                  <rect x="210" y="20" width="90" height="110" fill="rgba(59,130,246,0.15)" />
                  <text x="255" y="75" fill="#3b82f6" fontSize="4.5" textAnchor="middle" fontFamily="monospace">AIRE INTERIOR</text>

                  {/* Temperature Curve Overlay */}
                  {(() => {
                    // Map x coordinates for layers
                    const xAirOut = 15;
                    const xSurfOut = 40;
                    const xInsOut = 120;
                    const xDryOut = 180;
                    const xSurfIn = 210;
                    const xAirIn = 280;

                    // Map y coordinates corresponding to temperatures (scale: 15°C to 55°C -> y from 120 down to 40)
                    const tempToY = (temp: number) => {
                      const minTemp = 15;
                      const maxTemp = 55;
                      const ratio = (temp - minTemp) / (maxTemp - minTemp);
                      return 120 - ratio * 80;
                    };

                    const yAirOut = tempToY(solAirTemp);
                    const ySurfOut = tempToY(solAirTemp);
                    const yConcreteIn = tempToY(tConcreteIn);
                    const yInsulationIn = tempToY(tInsulationIn);
                    const yDrywallIn = tempToY(tDrywallIn);
                    const ySurfIn = tempToY(tSurfIn);
                    const yAirIn = tempToY(thermalInTemp);

                    const pointsPath = `
                      M ${xAirOut},${yAirOut} 
                      L ${xSurfOut},${ySurfOut} 
                      L ${xInsOut},${yConcreteIn} 
                      L ${xDryOut},${yInsulationIn} 
                      L ${xSurfIn},${yDrywallIn} 
                      L ${xAirIn},${yAirIn}
                    `;

                    return (
                      <>
                        <path d={pointsPath} fill="none" stroke="url(#heatGradient)" strokeWidth="3" strokeLinecap="round" />
                        
                        {/* Temperature tag notes */}
                        <circle cx={xSurfOut} cy={ySurfOut} r="3" fill="#ef4444" />
                        <text x={xSurfOut} y={ySurfOut - 6} fill="#ef4444" fontSize="4" textAnchor="middle" fontFamily="monospace" className="font-bold">{tSurfOut.toFixed(1)}°C</text>

                        <circle cx={xInsOut} cy={yConcreteIn} r="3" fill="#eab308" />
                        <text x={xInsOut} y={yConcreteIn - 6} fill="#eab308" fontSize="4" textAnchor="middle" fontFamily="monospace" className="font-bold">{tConcreteIn.toFixed(1)}°C</text>

                        {thermalInsulationInches > 0 && (
                          <>
                            <circle cx={xDryOut} cy={yInsulationIn} r="3" fill="#d97706" />
                            <text x={xDryOut} y={yInsulationIn - 6} fill="#d97706" fontSize="4" textAnchor="middle" fontFamily="monospace" className="font-bold">{tInsulationIn.toFixed(1)}°C</text>
                          </>
                        )}

                        <circle cx={xSurfIn} cy={ySurfIn} r="3" fill="#3b82f6" />
                        <text x={xSurfIn} y={ySurfIn - 6} fill="#3b82f6" fontSize="4" textAnchor="middle" fontFamily="monospace" className="font-bold">{tSurfIn.toFixed(1)}°C</text>
                      </>
                    );
                  })()}
                </svg>

                <div className="border-t border-border/20 pt-2 w-full text-[7.5px] text-muted-foreground uppercase flex justify-between font-mono">
                  <span>Pérdidas Q: {heatFlowQ.toFixed(1)} Watts</span>
                  <span>Ahorro Proyectado: {(savingsWatts / 1000).toFixed(3)} kW de carga térmica reducida</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

